import { useMutation, useOthers, useSelf, useStorage } from '@liveblocks/react'
import {
  useIsEditorReady,
  useLiveblocksExtension,
} from '@liveblocks/react-tiptap'
import type { Reference, Encounter } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import {
  isQuestionnaireAnswerOp,
  isValidatedToolOp,
  type PersistedQuestionnaireAnswerOp,
  pendingDecisionCount,
  questionnaireAnswerKey,
  type StagedSuggestion,
} from '@seen/core/models/compositionNote'
import type { NoteTemplate } from '@seen/core/models/noteTemplate'
import type { Participant } from '@seen/core/models/patients/participant'
import {
  deriveRecordStatusFromFieldDecisions,
  liveAfterRepend,
} from '@seen/core/models/suggestionStagingBasic'
import { decidableValidatedToolLinkIds } from '@seen/core/models/validatedToolFields'
import { isRecord } from '@seen/core/utils'
import { cn } from '@seen/ui'
import { useIsMutating } from '@tanstack/react-query'
import type { Transaction } from '@tiptap/pm/state'
import {
  type Editor,
  EditorContent,
  type Extensions,
  useEditor,
} from '@tiptap/react'
import DOMPurify from 'dompurify'
import {
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import {
  type BulkAcceptItemWrite,
  type BulkAcceptResult,
  hasValue,
} from '#src/components/care-plan/answerAccept.ts'
import {
  hideRemoteCollaborationCaretLabelOnFocus,
  tiptapVariants,
} from '#src/components/core/tiptapVariants.ts'
import { PlaceholderEditor } from '#src/components/PlaceholderEditor.tsx'
import {
  acceptSuggestion,
  reapplyAcceptSideEffects,
} from '#src/components/rich-text-editor/acceptSuggestionPipeline.ts'
import { AIProposalActionsProvider } from '#src/components/rich-text-editor/AIProposalActionsContext.tsx'
import { AIProposalHoverPopover } from '#src/components/rich-text-editor/AIProposalHoverPopover.tsx'
import { AI_PROPOSALS_PLUGIN_KEY } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { AIProposalStatusBar } from '#src/components/rich-text-editor/AIProposalStatusBar.tsx'
import { AIProposalUndoExtension } from '#src/components/rich-text-editor/AIProposalUndoExtension.ts'
import {
  isAcceptCompletionPatch,
  isAcceptInsert,
} from '#src/components/rich-text-editor/aiSuggestionStamp.ts'
import { AISuggestionTrailingParagraphExtension } from '#src/components/rich-text-editor/AISuggestionTrailingParagraphExtension.ts'
import { ConditionValueFeed } from '#src/components/rich-text-editor/conditional-display/ConditionValueFeed.tsx'
import { NodeConfigSheetHost } from '#src/components/rich-text-editor/conditional-display/NodeConfigSheetHost.tsx'
import { runUndoFlipCompensation } from '#src/components/rich-text-editor/conditionMentionSideEffects.ts'
import {
  clinicianDropReason,
  droppedSuggestionContent,
} from '#src/components/rich-text-editor/droppedSuggestionDetails.ts'
import { EditorParticipantContext } from '#src/components/rich-text-editor/EditorParticipantContext.ts'
import { EditorResourceProvider } from '#src/components/rich-text-editor/EditorResourceContext.tsx'
import { FloatingToolbar } from '#src/components/rich-text-editor/FloatingToolbar.tsx'
import {
  type EditorOutputFormat,
  getCleanedContent,
  stripPTagIfOneLine,
} from '#src/components/rich-text-editor/getCleanedContent.ts'
import {
  type IslandActions,
  IslandActionsProvider,
} from '#src/components/rich-text-editor/IslandActionsContext.tsx'
import { getIslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import { islandExitPos } from '#src/components/rich-text-editor/islandTraversal.ts'
import { IslandTraversalExtension } from '#src/components/rich-text-editor/IslandTraversalExtension.ts'
import {
  isMachineContentSync,
  setContentWithoutHistory,
} from '#src/components/rich-text-editor/machineContentSync.ts'
import {
  batchContentMetrics,
  classifyKey,
  classifyTransactionSource,
  createEmissionBudget,
  createReconcileDecisionThrottle,
  DIAGNOSTICS_COVERAGE_INTERVAL_MS,
  type DiagnosticsCounts,
  type KeyClass,
  largestStep,
  measureCaretDrift,
  observeSafely,
  reconcileContentStats,
  reportDiagnosticsCoverage,
  reportReconcileDecision,
  reportTransactionObserved,
  transactionTrigger,
} from '#src/components/rich-text-editor/noteEditorDiagnostics.ts'
import type { ObservationRecordStatus } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import {
  useRegistryVariableContexts,
  useSyncRegistryVariableContexts,
} from '#src/components/rich-text-editor/registry-variable/useRegistryVariableContexts.ts'
import { StagedSuggestionsBanner } from '#src/components/rich-text-editor/StagedSuggestionsBanner.tsx'
import { SuggestionRevealProvider } from '#src/components/rich-text-editor/SuggestionRevealContext.tsx'
import { createSuggestionSideEffectQueue } from '#src/components/rich-text-editor/suggestionSideEffectQueue.ts'
import { isYjsOrigin } from '#src/components/rich-text-editor/transactionOrigin.ts'
import { useEditorParticipant } from '#src/components/rich-text-editor/useEditorParticipant.ts'
import {
  type CollaborativeEditorAIMetadata,
  useOptionalCollaborativeEditorRegistry,
} from '#src/contexts/CollaborativeEditorRegistry.tsx'
import {
  type DroppedSuggestion,
  useAISuggestionHydrationForCollabHost,
  useUndoRejectRepaint,
} from '#src/hooks/useAISuggestionHydration.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useDeferredSuggestionReveal } from '#src/hooks/useDeferredSuggestionReveal.ts'
import {
  type DroppedQuestionnaireAtom,
  dismissDroppedQuestionnaireAtom,
  parseDroppedQuestionnaireAtomId,
  useDroppedQuestionnaireAtoms,
} from '#src/hooks/useDroppedQuestionnaireAtomsStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useHandleLiveblocksTiptapContentError } from '#src/hooks/useHandleContentError.ts'
import { useIsDocSynchronized } from '#src/hooks/useIsDocSynchronized.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import {
  type ReconcileDecision,
  useMountReconcile,
} from '#src/hooks/useMountReconcile.ts'
import { patchMutationTargetRef } from '#src/hooks/usePatchMutation.ts'
import {
  acceptAllQuestionnaireAnswers,
  reverseQuestionnaireBulkAccept,
  reversibleBulkAcceptLinkIds,
} from '#src/hooks/useQuestionnaireDecisionUndoStore.ts'
import {
  answerRefKey,
  type FieldDecisionEntry,
  type QuestionnaireAnswerRef,
  type RependOutcome,
  type SuggestionStatusFlip,
  useSuggestionPersistence,
} from '#src/hooks/useSuggestionPersistence.ts'
import { useTiptapExtensions } from '#src/hooks/useTiptapExtensions.ts'

type HTML = string

// Yjs/Liveblocks undo/redo applies a whole-doc ReplaceStep that tears down and
// rebuilds the editor DOM; the scroll container's scrollHeight momentarily
// collapses and the browser NATIVELY clamps scrollTop toward 0 (no
// scrollTo/scrollIntoView call is made, so it can't be gated). These two
// helpers snapshot the scroll offset on undo/redo and restore it across the
// next few frames once the rebuild settles. Extracted for unit-testability.
const SCROLL_DRIFT_THRESHOLD_PX = 8
// Re-clamp unconditionally for this many frames: the rebuild can re-collapse the
// scroll range a frame after the offset first sticks, so we can't stop early.
const SCROLL_RESTORE_MIN_FRAMES = 6
// Then keep chasing while the saved offset is still unreachable — a large note's
// node-view/image layout can take longer than the minimum window to settle —
// bounded so a legitimately shortened doc can't fight the scroll forever.
const SCROLL_RESTORE_MAX_FRAMES = 30

// The reason has already been rewritten for a clinician by clinicianDropReason
// and is a complete sentence, so the label (where the suggestion would have
// gone) is prepended rather than wrapped in another "couldn't add" phrase.
// Extracted for unit-testability.
export function notifyAcceptDrop(
  dropped: Pick<DroppedSuggestion, 'label' | 'reason'>
): void {
  toast.error(`${dropped.label}: ${dropped.reason}`)
}

// Does this document change warrant queueing the editor's autosave? Exported so
// the regression tests drive the real gate rather than a copy of it.
//
// With `saveOnAnyLocalEdit`, the rule is inverted: instead of enumerating the
// transactions we know to be worth saving, save every local document change and
// exclude only what arrived through Yjs. The whole bug class here is a doc
// change no branch claimed — a mouse-driven choice pick, a drag-to-reorder, a
// context-menu paste, a node view writing its own attributes — and an
// enumeration can only ever list the cases someone already found. Anything that
// changed the document and was not applied FROM Yjs was applied by this client
// and therefore is not yet on the server.
//
// `isYjsOrigin` rather than `isLocalForwardEdit` is deliberate: the latter also
// excludes undo/redo, and a local undo must still persist. It is also what keeps
// peers from double-saving — a remote peer's change reaches this client through
// Yjs carrying `isChangeOrigin`, so only the originating client saves. The
// keydown clause is kept anyway: it can only cause a redundant save (a peer's
// change landing within the keydown window), never a missed one.
//
// The one local doc change deliberately excluded is a machine content sync (a
// template seed or a Medplum->Liveblocks reconcile). It replays state the
// clinician never typed and the server already has, so saving it is at best a
// no-op — and on a surface where the reconcile can still run against a stale
// cache, saving it would persist the staleness instead of leaving it on screen.
export function shouldQueueSave(
  transaction: Transaction,
  isRightAfterKeyDown: boolean,
  saveOnAnyLocalEdit: boolean
): boolean {
  if (saveOnAnyLocalEdit) {
    if (isMachineContentSync(transaction)) {
      return false
    }
    return (
      isRightAfterKeyDown ||
      (transaction.docChanged && !isYjsOrigin(transaction))
    )
  }
  // Off, the gate is main's, plus the accept insert. An accept's `.focus()`
  // early-returns once the view is already focused, so in an "Accept all"
  // sweep only the first accept fires a focus event and the rest would queue
  // nothing — the clinician sees every edit and the server keeps the first.
  // The inverted branch above covers this by itself; naming it here is what
  // keeps AI accepts durable when `saveOnAnyLocalEdit` is off, so this feature
  // does not silently depend on that flag being flipped on.
  return (
    isRightAfterKeyDown ||
    isAcceptInsert(transaction) ||
    isAcceptCompletionPatch(transaction)
  )
}

/* The records a global "Reject all" decides: everything painted in the editor,
 * plus every pending record that never painted — questionnaire answers
 * included. Reject all rejects all: a control that dismissed the inline
 * proposals while quietly sparing the questionnaire cards still on screen left
 * the clinician to work out which two thirds of the review it had actually
 * done. Rejecting a suggested answer writes nothing into the
 * QuestionnaireResponse, so the sweep decides them without touching any
 * clinical text, and one Cmd+Z puts the whole batch back. */
export function rejectAllTargetIds(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op'>[],
  placedIds: readonly string[]
): string[] {
  const placedSet = new Set(placedIds)
  const unplacedIds = pending.flatMap((suggestion) =>
    placedSet.has(suggestion.id) ? [] : [suggestion.id]
  )
  return [...placedIds, ...unplacedIds]
}

/* One atom of a questionnaire record, as the sweep decided it. The record id
 * says which records this sweep covered, so its undo restores only those; the
 * questionnaire url and key say where to put the atom BACK, which is not the
 * same place — an AI run between the sweep and the undo retires that record and
 * carries its atoms into a fresh one. */
export interface RejectedQuestionnaireAtom {
  recordId: string
  questionnaireUrl: string
  atomKey: string
}

export interface QuestionnaireRejectAllPlan {
  /* The atoms the sweep decides: every answer still undecided when the
   * clinician pressed Reject all. An atom already ACCEPTED is left alone — the
   * sweep settles what is still open, it does not un-write an answer the
   * clinician has already patched into the response. */
  atoms: RejectedQuestionnaireAtom[]
  /* What each swept questionnaire record settles to once those atoms are
   * decided. Not always 'rejected': a record carrying an earlier accept is
   * finished ACCEPTED, and stamping it rejected would deny an answer that is
   * sitting in the response. */
  statuses: SuggestionStatusFlip[]
}

/* The per-atom half of a "Reject all", which record-level statuses alone cannot
 * express: `fieldDecisions` is where a questionnaire answer's decision lives, so
 * a sweep that only flipped the record would hide the cards while leaving every
 * atom durably reading pending. */
export function rejectAllQuestionnaireDecisions(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op' | 'fieldDecisions'>[]
): QuestionnaireRejectAllPlan {
  const atoms: RejectedQuestionnaireAtom[] = []
  const statuses: SuggestionStatusFlip[] = []
  for (const suggestion of pending) {
    if (!isQuestionnaireAnswerOp(suggestion.op)) {
      continue
    }
    let hasAccepted = false
    for (const answer of suggestion.op.answers) {
      const atomKey = questionnaireAnswerKey(answer)
      const decision = suggestion.fieldDecisions?.[atomKey] ?? 'pending'
      if (decision === 'accepted') {
        hasAccepted = true
      } else if (decision === 'pending') {
        atoms.push({
          recordId: suggestion.id,
          questionnaireUrl: suggestion.op.questionnaireUrl,
          atomKey,
        })
      }
    }
    statuses.push({
      id: suggestion.id,
      status: hasAccepted ? 'accepted' : 'rejected',
    })
  }
  return { atoms, statuses }
}

/* The staging writes a bulk decision and its undo need. Both projections come
 * from the editor's own `useSuggestionPersistence`, which is deliberate: see
 * `rejectedAtomsRef` for why a reject sweep's restore never goes through the
 * questionnaire's per-instance executor registry. An accept sweep does need
 * that registry, but only for the answer patches — its staging still lands
 * here. */
export interface BulkDecisionProjections {
  projectStatuses: (flips: SuggestionStatusFlip[]) => void
  /* Batched, never per atom: the staging writes are serialized through one
   * promise chain, so a sweep that called this once per answer issued two dozen
   * PATCHes in a row and the clinician watched the cards clear one at a time. */
  projectFieldDecisions: (entries: FieldDecisionEntry[]) => void
  /* Return questionnaire answers to undecided, addressed by KEY so each lands
   * on whichever record holds that questionnaire's live slot now. A reversal
   * cannot use the record id it swept: an AI run between the sweep and the undo
   * supersedes that record, and the write would go nowhere while the
   * compensating patch still took the answers back out of the plan. Batched for
   * the same reason the decisions are — one clinician action, one write. */
  rependQuestionnaireAnswers: (
    entries: QuestionnaireAnswerRef[]
  ) => Promise<RependOutcome>
}

/* One subscale of a validated-tool record, as a sweep decided it. Addressed by
 * record id (not, as a questionnaire answer is, by slot): a validated tool's
 * review lives in one node view reading one record, and its undo puts the
 * decision back where the sweep made it. */
export interface DecidedValidatedToolField {
  recordId: string
  linkId: string
}

interface ValidatedToolBulkPlan {
  fields: DecidedValidatedToolField[]
  statuses: SuggestionStatusFlip[]
}

/* The per-subscale half of a bulk decision over validated-tool records, which
 * record-level statuses alone cannot express: a subscale's decision lives in
 * `fieldDecisions`, so a sweep that only flipped the record would clear the
 * tool's controls while leaving every subscale durably reading pending.
 *
 * Only STILL-PENDING subscales are swept. One the clinician already decided is
 * left as they left it — the same rule the questionnaire sweep follows — and
 * the record's settled status is derived from the whole set afterwards, so a
 * "Reject all" over a tool holding an earlier accept finishes ACCEPTED rather
 * than denying an answer the clinician had kept. */
export function validatedToolBulkPlan(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op' | 'fieldDecisions'>[],
  status: 'accepted' | 'rejected'
): ValidatedToolBulkPlan {
  const fields: DecidedValidatedToolField[] = []
  const statuses: SuggestionStatusFlip[] = []
  for (const suggestion of pending) {
    if (!isValidatedToolOp(suggestion.op)) {
      continue
    }
    /* Only the answers this tool's form can render: one naming a field it does
     * not have decides nothing, so a sweep must neither claim it nor wait on it
     * before settling the record. */
    const linkIds = decidableValidatedToolLinkIds(suggestion.op)
    const next = { ...suggestion.fieldDecisions }
    const swept: string[] = []
    for (const linkId of linkIds) {
      if ((suggestion.fieldDecisions?.[linkId] ?? 'pending') !== 'pending') {
        continue
      }
      swept.push(linkId)
      next[linkId] = status
    }
    if (swept.length === 0) {
      continue
    }
    for (const linkId of swept) {
      fields.push({ recordId: suggestion.id, linkId })
    }
    statuses.push({
      id: suggestion.id,
      status: deriveRecordStatusFromFieldDecisions(linkIds, next),
    })
  }
  return { fields, statuses }
}

/* Write a validated-tool sweep's decisions, and hand back the restore that
 * takes them off again. Staging only: accepting a subscale fills the tool's
 * form, and the QuestionnaireResponse is still created by the clinician
 * pressing Submit, so there is no clinical write to compensate for and the
 * undo is a plain re-pend. */
export function applyAcceptAllValidatedTools(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op' | 'fieldDecisions'>[],
  projections: Pick<
    BulkDecisionProjections,
    'projectStatuses' | 'projectFieldDecisions'
  >
): (() => void) | null {
  const { fields, statuses } = validatedToolBulkPlan(pending, 'accepted')
  if (fields.length === 0) {
    return null
  }
  projections.projectFieldDecisions(
    fields.map((field) => ({
      id: field.recordId,
      atomKey: field.linkId,
      status: 'accepted' as const,
    }))
  )
  projections.projectStatuses(statuses)
  return () => {
    projections.projectFieldDecisions(
      fields.map((field) => ({
        id: field.recordId,
        atomKey: field.linkId,
        status: 'pending' as const,
      }))
    )
    projections.projectStatuses(
      statuses.map((flip) => ({ id: flip.id, status: 'pending' as const }))
    )
  }
}

interface ApplyRejectAllResult {
  ids: string[]
  atoms: RejectedQuestionnaireAtom[]
  /* The validated-tool subscales this sweep decided, so its undo re-pends
   * exactly those and leaves alone any the clinician had settled by hand. */
  validatedToolFields: DecidedValidatedToolField[]
  /* The swept records whose status the undo must NOT flip back itself: a
   * questionnaire record is made pending by re-pending one of its answers, on
   * whichever record holds the slot by then. Flipping the swept id as well
   * would re-pend a record a later run has left behind, beside the live one. */
  questionnaireIds: string[]
}

/* Dismiss every pending suggestion. Returns what the batch covered, so its undo
 * can put back exactly that: the record ids, the questionnaire atoms and the
 * validated-tool subscales this sweep decided (never the ones the clinician had
 * settled beforehand). */
export function applyRejectAll(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op' | 'fieldDecisions'>[],
  placedIds: readonly string[],
  projections: BulkDecisionProjections
): ApplyRejectAllResult {
  const ids = rejectAllTargetIds(pending, placedIds)
  const plan = rejectAllQuestionnaireDecisions(pending)
  const toolPlan = validatedToolBulkPlan(pending, 'rejected')
  /* Fields before records: a record's status is DERIVED from its field
   * decisions, so settling the record first would leave the durable record
   * contradicting itself for as long as the second write takes. One write for
   * both kinds — the sweep is one clinician action. */
  const decisions = [
    ...plan.atoms.map((atom) => ({
      id: atom.recordId,
      atomKey: atom.atomKey,
      status: 'rejected' as const,
    })),
    ...toolPlan.fields.map((field) => ({
      id: field.recordId,
      atomKey: field.linkId,
      status: 'rejected' as const,
    })),
  ]
  if (decisions.length > 0) {
    projections.projectFieldDecisions(decisions)
  }
  const settled = new Map(
    [...plan.statuses, ...toolPlan.statuses].map((flip) => [
      flip.id,
      flip.status,
    ])
  )
  if (ids.length > 0) {
    projections.projectStatuses(
      ids.map((id) => ({ id, status: settled.get(id) ?? 'rejected' }))
    )
  }
  return {
    ids,
    atoms: plan.atoms,
    validatedToolFields: toolPlan.fields,
    questionnaireIds: plan.statuses.map((flip) => flip.id),
  }
}

/* Put a whole batch back to pending. Atoms first again, so a questionnaire's
 * cards return with its record rather than a beat later. */
export function applyUndoRejectAll(
  ids: readonly string[],
  atoms: readonly RejectedQuestionnaireAtom[],
  questionnaireIds: readonly string[],
  projections: BulkDecisionProjections,
  validatedToolFields: readonly DecidedValidatedToolField[] = []
): string[] {
  const batch = new Set(ids)
  const restored = atoms.filter((atom) => batch.has(atom.recordId))
  void projections.rependQuestionnaireAnswers(
    restored.map((atom) => ({
      questionnaireUrl: atom.questionnaireUrl,
      atomKey: atom.atomKey,
    }))
  )
  /* The validated-tool half needs no slot resolution: its review reads one
   * record, so the subscales go back on the record the sweep decided them on,
   * and the record's own re-pend rides the id flips below. */
  const toolFields = validatedToolFields.filter((field) =>
    batch.has(field.recordId)
  )
  if (toolFields.length > 0) {
    projections.projectFieldDecisions(
      toolFields.map((field) => ({
        id: field.recordId,
        atomKey: field.linkId,
        status: 'pending' as const,
      }))
    )
  }
  /* Questionnaire records are left to the re-pend above, which puts each answer
   * back on the record holding its slot and makes THAT one pending. Flipping
   * the swept id too would re-pend a record a later run superseded past, and
   * two pending records for one questionnaire is what this whole change exists
   * to prevent. */
  const byRepend = new Set(questionnaireIds)
  const flips = ids.filter((id) => !byRepend.has(id))
  if (flips.length > 0) {
    projections.projectStatuses(flips.map((id) => ({ id, status: 'pending' })))
  }
  /* The ids this call actually flips — the starting point for what a repaint
   * waits for, which the caller narrows again to the ones that can arrive.
   * A questionnaire record re-pended by key may never return to the pending set
   * under the id that was swept — the answer lands on whichever record holds the
   * slot — so waiting on the whole sweep would wait forever and the note's own
   * decorations would stay unpainted. */
  return flips
}

/* One questionnaire's share of an "Accept all": the staging record its answers
 * belong to, and the atoms still undecided when the clinician pressed it.
 *
 * An atom the clinician had already decided is left exactly as they left it —
 * the same rule "Reject all" follows. A rejected answer stays rejected rather
 * than being swept to accepted, which would write a value they had just
 * refused; an accepted one is already in the response.
 *
 * An answer carrying no usable value is dropped here rather than written. The
 * setter reads an empty value as "delete this answer", so the single accept
 * refuses those, and a sweep that did not would erase authored text the AI had
 * nothing to say about. They stay undecided, and the record still settles: the
 * status derivation has never counted them. */
export interface QuestionnaireAcceptTarget {
  recordId: string
  questionnaireUrl: string
  answers: PersistedQuestionnaireAnswerOp['answers']
}

export function acceptAllQuestionnaireTargets(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op' | 'fieldDecisions'>[]
): QuestionnaireAcceptTarget[] {
  const targets: QuestionnaireAcceptTarget[] = []
  for (const suggestion of pending) {
    if (!isQuestionnaireAnswerOp(suggestion.op)) {
      continue
    }
    const answers = suggestion.op.answers.filter((answer) => {
      const decision =
        suggestion.fieldDecisions?.[questionnaireAnswerKey(answer)] ?? 'pending'
      /* No item to gate on here: the sweep is planned across every staged
       * record, and only the mounted questionnaire holds the items. The write
       * itself asks again with the item in hand, and drops what it cannot
       * take. */
      return decision === 'pending' && hasValue(answer, undefined)
    })
    if (answers.length > 0) {
      targets.push({
        recordId: suggestion.id,
        questionnaireUrl: suggestion.op.questionnaireUrl,
        answers,
      })
    }
  }
  return targets
}

// What one questionnaire's sweep wrote, held so the batch's undo can compensate
// for exactly that.
export interface AcceptedQuestionnaireEntry {
  questionnaireUrl: string
  atomKeys: string[]
  writes: BulkAcceptItemWrite[]
}

/* Write every still-undecided questionnaire answer the sweep covers.
 *
 * One patch per QUESTIONNAIRE RESPONSE. All of a questionnaire's items live in
 * that one resource, so the executor applies every atom of every item in a
 * single pass (`applyBulkAccept`) and patches once: atomic, and one
 * optimistic-concurrency test rather than one per item. A care plan holds
 * several questionnaires, which are separate resources, so a sweep spanning
 * more than one still issues a patch each — taken in turn rather than
 * concurrently, so two writes cannot interleave against the same cache.
 *
 * Staging is projected only once a write has LANDED, exactly as the single
 * accept does: an eager flip would consume the proposal on a failed PATCH and
 * leave the answer nowhere. The record is finished accepted only where the
 * sweep placed every atom it was given; one it could not place stays pending,
 * and so does the record holding it. */
export async function applyAcceptAllQuestionnaires(
  pending: readonly Pick<StagedSuggestion, 'id' | 'op' | 'fieldDecisions'>[],
  projections: BulkDecisionProjections
): Promise<AcceptedQuestionnaireEntry[]> {
  const accepted: AcceptedQuestionnaireEntry[] = []
  /* Accumulated across every questionnaire the sweep reaches and flushed once
   * below: the whole sweep is one clinician action, so it earns one staging
   * write however many questionnaires and answers it spans. */
  const decisions: FieldDecisionEntry[] = []
  const statuses: SuggestionStatusFlip[] = []
  for (const target of acceptAllQuestionnaireTargets(pending)) {
    const { writes, unplaceableAtomKeys } = await new Promise<BulkAcceptResult>(
      (resolve) => {
        /* False means no questionnaire is mounted for this URL, so nothing can
         * be written and its atoms stay undecided — the sweep decides what it
         * can reach and never claims what it cannot. */
        if (
          !acceptAllQuestionnaireAnswers(
            target.questionnaireUrl,
            target.answers,
            resolve
          )
        ) {
          resolve({ writes: [], unplaceableAtomKeys: [] })
        }
      }
    )
    const atomKeys = writes.flatMap((write) => write.atomKeys)
    if (atomKeys.length === 0) {
      continue
    }
    for (const atomKey of atomKeys) {
      decisions.push({
        id: target.recordId,
        atomKey,
        status: 'accepted' as const,
      })
    }
    /* The record is finished only once nothing it holds is still to be decided.
     * An atom the sweep could not place stays pending, and settling the record
     * around it would take the whole suggestion surface away — leaving that
     * goal or intervention with no card to accept it from, which is the silent
     * drop this sweep exists to avoid. The single accept holds the same line
     * through `deriveQuestionnaireRecordStatus`. */
    if (unplaceableAtomKeys.length === 0) {
      statuses.push({ id: target.recordId, status: 'accepted' as const })
    }
    accepted.push({
      questionnaireUrl: target.questionnaireUrl,
      atomKeys,
      writes,
    })
  }
  if (decisions.length > 0) {
    projections.projectFieldDecisions(decisions)
    projections.projectStatuses(statuses)
  }
  return accepted
}

/* Take a bulk accept back. Unlike the reject sweep this cannot be a state flip:
 * the answers are in the QuestionnaireResponse, so each item needs a
 * compensating patch, which only a mounted questionnaire can make.
 *
 * PREFLIGHT, then staging, then the answers — the same order the single-atom
 * undo takes, and for the same reason: the two writes cannot be made atomic, so
 * the batch is settled against the answers as they stand BEFORE anything is
 * written. An item whose answer has moved since the sweep, or whose
 * questionnaire is not mounted, is left out of the batch entirely rather than
 * re-pended and then found unreversible.
 *
 * PARTIAL REVERSAL survives that. Whatever the preflight accepts is re-pended
 * and reversed; whatever it does not stays ACCEPTED, and the count of those
 * atoms is returned so the caller can say so. Re-pending an atom whose value is
 * still sitting in the field would describe the plan wrongly — its card would
 * come back proposing text the answer already holds. The batch is spent either
 * way: its snapshots describe the answers as the sweep found them, and a second
 * press would plan against a response that has since moved. */
export async function applyUndoAcceptAllQuestionnaires(
  accepted: readonly AcceptedQuestionnaireEntry[],
  projections: BulkDecisionProjections
): Promise<number> {
  let unreversed = 0
  // Accumulated and flushed once, for the same reason the sweep itself is.
  const repends: QuestionnaireAnswerRef[] = []
  const reversible: {
    questionnaireUrl: string
    writes: BulkAcceptItemWrite[]
  }[] = []
  for (const entry of accepted) {
    const canReverse = new Set(
      reversibleBulkAcceptLinkIds(entry.questionnaireUrl, entry.writes)
    )
    const writes = entry.writes.filter((write) => canReverse.has(write.linkId))
    const atomKeys = writes.flatMap((write) => write.atomKeys)
    for (const atomKey of atomKeys) {
      /* By key, not by the swept record id: re-pending an atom makes its record
       * pending by definition, and only the live record can show the card. */
      repends.push({ questionnaireUrl: entry.questionnaireUrl, atomKey })
    }
    reversible.push({ questionnaireUrl: entry.questionnaireUrl, writes })
    unreversed += entry.atomKeys.length - atomKeys.length
  }
  if (repends.length === 0) {
    return unreversed
  }
  /* Staging first. A failed staging write leaves every accepted value in the
   * plan exactly as it stands, which is the safe half of the two. */
  const outcome = await projections.rependQuestionnaireAnswers(repends)
  if (!outcome.ok) {
    return accepted.reduce((total, entry) => total + entry.atomKeys.length, 0)
  }
  /* `ok` says the staging write landed, NOT that these atoms have cards again:
   * a re-pend no-ops for an answer no record holds any more, and a whole batch
   * no-ops when there is no staging loaded, both reporting ok. So a write is
   * taken back only where EVERY atom it covers is undecided again. One that is
   * not stays accepted and is counted below — the value standing in the plan is
   * the safe half; the reversal is what could take it out with nothing left to
   * put it back. */
  const undecided = new Set(outcome.repended.map(answerRefKey))
  const restorable = reversible.map((entry) => ({
    questionnaireUrl: entry.questionnaireUrl,
    writes: entry.writes.filter((write) =>
      write.atomKeys.every((atomKey) =>
        undecided.has(
          answerRefKey({ questionnaireUrl: entry.questionnaireUrl, atomKey })
        )
      )
    ),
  }))
  const reversed = new Set<string>()
  for (const entry of restorable) {
    if (entry.writes.length === 0) {
      continue
    }
    const reversedLinkIds = await new Promise<string[]>((resolve) => {
      if (
        !reverseQuestionnaireBulkAccept(
          entry.questionnaireUrl,
          entry.writes,
          resolve
        )
      ) {
        resolve([])
      }
    })
    const restored = new Set(reversedLinkIds)
    for (const write of entry.writes) {
      if (restored.has(write.linkId)) {
        for (const atomKey of write.atomKeys) {
          reversed.add(
            answerRefKey({ questionnaireUrl: entry.questionnaireUrl, atomKey })
          )
        }
      }
    }
  }
  /* The preflight judged these reversible, so a shortfall is the answer moving
   * under the write — one questionnaire's patch can land while another's does
   * not. Put back ONLY the atoms whose value is still in the plan: reverting
   * one already taken out would remove its card too, which is the outcome this
   * ordering exists to prevent. */
  const stranded = repends.filter((ref) => !reversed.has(answerRefKey(ref)))
  if (stranded.length > 0) {
    outcome.revert(stranded)
  }
  return unreversed + stranded.length
}

/* Said when a bulk accept could not be fully taken back. The parts that could
 * be are, and the rest stand written — see `applyUndoAcceptAllQuestionnaires`
 * for why leaving them accepted is the honest half of that outcome. */
export const BULK_ACCEPT_NOT_UNDONE =
  'Some suggested answers could not be undone, so they are still in the care plan.'

/* The listed couldn't-be-placed questionnaire atoms that belong to THIS
 * editor's staging: the list is page-wide, while the bar counts and dismisses
 * only its own records. An atom is kept only while it is still undecided on a
 * still-pending record, so a row never outlives its decision by the render it
 * takes the questionnaire to withdraw it. */
export function ownQuestionnaireDrops(
  pending: readonly Pick<
    StagedSuggestion,
    'id' | 'op' | 'fieldDecisions' | 'status'
  >[],
  rows: readonly DroppedQuestionnaireAtom[]
): DroppedQuestionnaireAtom[] {
  if (rows.length === 0) {
    return []
  }
  const records = new Map(
    pending.map((suggestion) => [suggestion.id, suggestion])
  )
  return rows.filter((row) => {
    const record = records.get(row.recordId)
    return (
      record !== undefined &&
      record.status === 'pending' &&
      isQuestionnaireAnswerOp(record.op) &&
      (record.fieldDecisions?.[row.atomKey] ?? 'pending') === 'pending'
    )
  })
}

/* Route the couldn't-be-placed dialog's dismissals: a note suggestion's row is
 * its whole record, rejected through `rejectRecords`; a questionnaire row is a
 * single atom, decided by the questionnaire that listed it. */
export function dismissDroppedRows(
  ids: readonly string[],
  rejectRecords: (ids: string[]) => void,
  dismissQuestionnaireAtom: (id: string) => boolean
): void {
  const recordIds: string[] = []
  for (const id of ids) {
    if (parseDroppedQuestionnaireAtomId(id) === null) {
      recordIds.push(id)
    } else {
      /* Never handed on as a record id when the row has gone: there is no
       * record by that id, and the atom it named was decided or placed. */
      dismissQuestionnaireAtom(id)
    }
  }
  if (recordIds.length > 0) {
    rejectRecords(recordIds)
  }
}

/* The questionnaire atoms a bulk control would decide. A SUBSET of
 * `pendingUnpaintedCount` — which the status bar already adds to its painted
 * count — so this is what the controls are shown FOR, never something added to
 * that total again. Without it a care plan whose suggestions are all
 * questionnaire answers showed no bulk controls at all, the decoration count
 * being zero. */
export function pendingQuestionnaireAtomCount(
  pending: readonly StagedSuggestion[]
): number {
  return pendingDecisionCount(
    pending.filter((suggestion) => isQuestionnaireAnswerOp(suggestion.op))
  )
}

/* The validated-tool subscales a bulk control would decide. A SUBSET of
 * `pendingUnpaintedCount`, on the same terms as the questionnaire count above:
 * what the controls are shown FOR, never something added to that total again.
 * Without it a note whose only review is a staged Braden showed no bulk
 * controls at all, the decoration count being zero. */
export function pendingValidatedToolAtomCount(
  pending: readonly StagedSuggestion[]
): number {
  return pendingDecisionCount(
    pending.filter((suggestion) => isValidatedToolOp(suggestion.op))
  )
}

/* The decisions the status bar must ADD to its painted-decoration count: the
 * pending records that never paint as decorations — validated-tool records (the
 * node view carries their review UI) and questionnaire records (decided atom by
 * atom in the questionnaire itself). Passing the whole pending set to
 * `pendingDecisionCount` would double-count the inline suggestions the editor
 * already paints. Questionnaire records contribute one unit per still-undecided
 * atom, so the bar keeps counting them after reveal instead of dropping them. */
export function pendingUnpaintedCount(
  pending: readonly StagedSuggestion[]
): number {
  return pendingDecisionCount(
    pending.filter(
      (suggestion) =>
        isValidatedToolOp(suggestion.op) ||
        isQuestionnaireAnswerOp(suggestion.op)
    )
  )
}

/* Render-time facts the caret-jump diagnostics need from inside `onTransaction`,
 * which closes over its scope at `useEditor` init time. Several of them (room
 * presence, doc sync) are only known further down the render, so they are
 * written to a ref during render rather than captured. */
interface NoteDiagnosticsContext {
  enabled: boolean
  contentVersionId: string | null
  othersCount: number
  isDocSynchronized: boolean
  saveOnAnyLocalEdit: boolean
}

export function isUndoRedoShortcut(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey'>
): boolean {
  const key = event.key.toLowerCase()
  return (event.metaKey || event.ctrlKey) && (key === 'z' || key === 'y')
}

export function restoreScrollTop(
  viewport: Pick<HTMLElement, 'scrollTop'>,
  saved: number,
  frame = 0,
  requestFrame: (callback: () => void) => void = requestAnimationFrame
): void {
  if (Math.abs(viewport.scrollTop - saved) > SCROLL_DRIFT_THRESHOLD_PX) {
    viewport.scrollTop = saved
  }
  // The assignment above is clamped to the current max scroll, so scrollTop only
  // lands on `saved` once the rebuilt DOM is tall enough again.
  const reached =
    Math.abs(viewport.scrollTop - saved) <= SCROLL_DRIFT_THRESHOLD_PX
  const next = frame + 1
  if (
    next < SCROLL_RESTORE_MIN_FRAMES ||
    (!reached && next < SCROLL_RESTORE_MAX_FRAMES)
  ) {
    requestFrame(() => restoreScrollTop(viewport, saved, next, requestFrame))
  }
}

/* Arm the restore for an undo/redo about to run. Both entry points need it: the
 * host's own keydown, and the island bridge below — a keystroke made inside a
 * suggestion widget never reaches `handleDOMEvents` (the widget's `stopEvent`
 * gate sits ahead of it), so a forwarded undo has to re-supply the snapshot the
 * host would have taken for itself. */
function armScrollRestore(view: { dom: HTMLElement }): void {
  const viewport = view.dom.closest<HTMLElement>(
    '[data-radix-scroll-area-viewport]'
  )
  const saved = viewport?.scrollTop ?? 0
  if (viewport && saved > SCROLL_DRIFT_THRESHOLD_PX) {
    restoreScrollTop(viewport, saved)
  }
}

/**
 * Controlled deferred-paint reveal state, supplied by a host that owns the
 * staged-suggestions banner (so it can render outside a suspense boundary).
 * Shape mirrors {@link useDeferredSuggestionReveal}'s return minus the handlers
 * the editor doesn't call. The host passes its `handleReveal` as `onReview`
 * only because it renders the banner; the editor uses `revealed`/`revealGen`
 * for hydration and `onHide` for the status bar's Hide control.
 */
export interface DeferredRevealProps {
  revealed: boolean
  /** A reveal was requested but the hydration paint hasn't run yet. */
  revealing: boolean
  revealGen: number
  /**
   * Reported once the hydration paint's preconditions hold in this editor, which
   * is what clears the host banner's loading state. The editor owns this signal
   * because the gating facts — collab readiness and initial doc sync — only
   * exist inside the suspense boundary the host renders its banner outside of.
   */
  onRevealSettled: () => void
  /** Status-bar "Hide": un-paint decorations and return to the banner. */
  onHide: () => void
  /**
   * Live AI staging painted new proposals into this editor. Runs the host's own
   * reveal (the banner's "Review suggestions" action) when the note is still
   * deferred, so paint stays all-or-nothing.
   */
  onLiveStaged: () => void
}

/* Structured capture decides which plugins the editor installs, and TipTap
 * builds those once, so a live flag change (the admin override) rebuilds the
 * editor. The note's content lives in the collaboration room and survives. */
export function CollaborativeEditor(
  props: Parameters<typeof CollaborativeEditorInner>[0]
): ReactNode {
  const structuredCapture = useFeatureFlag(featureFlags.structuredCapture)
  return (
    <CollaborativeEditorInner
      key={structuredCapture ? 'structured-capture' : 'plain'}
      {...props}
    />
  )
}

function CollaborativeEditorInner({
  initialContent,
  onChange,
  fileHandlerDocumentReferenceContextRelated,
  field,
  contentVersionId,
  placeholder = 'Type anything…',
  editorRef,
  flushPendingSaveRef,
  aiMetadata,
  aiProposalStickyTopClassName,
  outputFormat = 'html',
  participant,
  encounter,
  deferredReveal,
  onMergeTemplateMetadata,
  additionalExtensions,
  observationRecordStatus,
}: {
  initialContent: HTML
  onChange: (content: string) => any
  fileHandlerDocumentReferenceContextRelated?: Reference[]
  field?: string
  /* Medplum `meta.versionId` of the resource `initialContent` came from.
   * Diagnostics only: it joins this mount's events to the resource history. */
  contentVersionId?: string
  placeholder?: string
  editorRef?: MutableRefObject<Editor | null>
  /**
   * When set, runs a fresh `onChange` with the current editor content and
   * awaits it, cancelling any pending debounced save.
   */
  flushPendingSaveRef?: MutableRefObject<(() => Promise<void>) | null>
  /** When set, registers this editor with the AI registry so Seen AI can read & propose edits. */
  aiMetadata?: Omit<CollaborativeEditorAIMetadata, 'readOnly'>
  /**
   * Sticky offset for the AI proposal status bar, forwarded to
   * `AIProposalStatusBar`. Defaults to `top-4` there. Pass a header-clearing
   * offset (e.g. `top-16`) when this editor renders in the shared page
   * ScrollArea alongside the `sticky z-50` patient nav header, so the bar pins
   * below it rather than behind it.
   */
  aiProposalStickyTopClassName?: string
  /* Persistence format for onChange. 'html' (default): sanitized HTML.
   * 'json': JSON.stringify(editor.getJSON()). Internal Liveblocks reconciliation
   * stays HTML-based — callers must pre-convert JSON `initialContent` to HTML. */
  outputFormat?: EditorOutputFormat
  /* Mirrors `RichTextEditor.participant`. Flows into
   * `useTiptapExtensions({ contextParticipant })`, which configures
   * participant-aware nodes (encounter-medications-block,
   * observation, registryVariable). Omit for editor mounts that have no
   * participant in scope (memos, sticky notes, care plan blocks, desks). */
  participant?: Participant
  /* The encounter the note documents. Registry variables bound to the
   * encounter context resolve from it; omit when the host has none. */
  encounter?: Encounter
  /**
   * Controlled deferred-paint (S5) reveal lifecycle, hoisted to a host that
   * renders this editor inside a Liveblocks suspense boundary (the visit-note
   * page) so the staged-suggestions banner can paint without waiting for the
   * editor to boot. When provided, the host owns `revealed`/`revealGen` + the
   * banner; this editor consumes the threaded state to drive hydration and gate
   * the status bar / hover popover, and renders NO banner of its own. When
   * omitted (memos, sticky notes, care-plan blocks), this editor falls back to
   * owning the reveal lifecycle and rendering its own banner — behavior is
   * identical to before this prop existed.
   */
  deferredReveal?: DeferredRevealProps
  /* Called when a note template is inserted via @-mention, so the host can union
   * the template's labels + categories into the note. The mention extension only
   * holds the editor, not the Composition; the host owns the label/category write
   * path. Omit for editor mounts with no host note (memos, sticky notes, the
   * template editor) — the extension then no-ops the merge. */
  onMergeTemplateMetadata?: (template: NoteTemplate) => void
  /* Mirrors `RichTextEditor.additionalExtensions`. Appended to the built-in
   * extension list at editor construction — and only there. `useEditor` runs
   * with its default empty `deps`, so a changed option set goes to
   * `editor.setOptions()`, which merges options and refreshes the view but
   * never rebuilds the extension manager or the schema (@tiptap 3.22.5). A new
   * array is therefore silently ignored rather than remounting anything: pass a
   * stable identity, and have extensions read late-arriving values through refs
   * or transaction meta instead of options. */
  additionalExtensions?: Extensions
  /* Status observation nodes create records with. The visit note passes
   * `preliminary`; signing finalizes them. */
  observationRecordStatus?: ObservationRecordStatus
}): ReactNode {
  /* Test-visible autosave status, surfaced as `data-save-state` on the editor
   * wrapper. Wraps `onChange` without altering its behavior: it sets 'saving'
   * before the save and resolves to 'saved'/'error' off the same promise the
   * caller already awaits, so persistence timing is unchanged. */
  const [saveState, setSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle')
  /* Caret-jump diagnostics (observation only — see noteEditorDiagnostics). Every
   * one of these is inert until the flag turns on. `lastSaveAt` is stamped when
   * a save is ISSUED, because that is the document the server will hold: a local
   * edit made after that stamp is genuinely unsaved even if the request has not
   * settled. */
  const noteDiagnosticsRef = useRef<NoteDiagnosticsContext>({
    enabled: false,
    contentVersionId: null,
    othersCount: 0,
    isDocSynchronized: false,
    saveOnAnyLocalEdit: false,
  })
  /* Separates one editor mount from the next. `noteId` names the document and is
   * what joins these events to a reported incident; `mountId` is what tells a
   * remount apart from a continuing mount, and two tabs on one note apart from
   * one tab, neither of which `noteId` can distinguish on its own. */
  const [mountId] = useState(() => crypto.randomUUID())
  const [mountedAt] = useState(() => Date.now())
  /* Cumulative for the life of the mount: the denominator that makes a quiet
   * transaction stream interpretable, and the drop count that says whether the
   * recorded stream is complete. */
  const diagnosticsCountsRef = useRef<DiagnosticsCounts>({
    transactions: 0,
    docChangedTransactions: 0,
    selectionSetTransactions: 0,
    keyDowns: 0,
    pointerDowns: 0,
    driftTriggers: 0,
    replaceTriggers: 0,
    transactionsEmitted: 0,
    transactionsDropped: 0,
    reconcileEvaluations: 0,
    reconcileDecisionsEmitted: 0,
  })
  const emissionBudgetRef = useRef(createEmissionBudget())
  const pointerDownAtRef = useRef<number | null>(null)
  const pointerDownInsideEditorRef = useRef<boolean | null>(null)
  const lastKeyClassRef = useRef<KeyClass | null>(null)
  const caretBeforeRef = useRef<number | null>(null)
  const lastLocalEditAtRef = useRef<number | null>(null)
  const lastSaveAtRef = useRef<number | null>(null)
  const lastReplaceAtRef = useRef<number | null>(null)
  /* Flips once, on this mount's first document change of its own. State as well
   * as a ref: the reconcile effect has to re-run when it flips, and the ref
   * keeps the per-transaction check to a single read afterwards. */
  const [hasLocalEdit, setHasLocalEdit] = useState(false)
  const hasLocalEditRef = useRef(false)

  const trackedOnChange = useCallback(
    (content: string) => {
      lastSaveAtRef.current = Date.now()
      setSaveState('saving')
      const result = onChange(content)
      void Promise.resolve(result).then(
        () => setSaveState('saved'),
        () => setSaveState('error')
      )
      return result
    },
    [onChange]
  )

  /* `onFocus` and `onUpdate` close over `outputFormat` at `useEditor` init
   * time — TipTap doesn't re-register event handlers on re-render. Route the
   * value through the ref that `saveLiveContent` reads, the single
   * serialization path every persistence call-site now goes through, so they
   * agree even if a caller ever toggles the format mid-session. Today all
   * callers pass a literal. */
  const outputFormatRef = useRef(outputFormat)
  outputFormatRef.current = outputFormat

  /* The live editor, declared here because everything below `useEditor` and the
   * debounced save above it both need it: the save (assigned during render right
   * after the editor is built, so it is current long before any save fires) and
   * the deferred-reveal `getEditor` further down. */
  const liveEditorRef = useRef<Editor | null>(null)

  /* Serializes the document WHEN THE SAVE FIRES, not when it is queued. The
   * debounce exists to collapse a burst into one trailing call, and that call
   * must carry the document as it stands then — otherwise how much of the burst
   * survives depends on how many times the gate happened to re-queue and refresh
   * the argument, which is exactly the thing the gate gets wrong. */
  const saveLiveContent = useCallback(() => {
    const liveEditor = liveEditorRef.current
    if (!liveEditor || liveEditor.isDestroyed) {
      return
    }
    return trackedOnChange(
      getCleanedContent(liveEditor, outputFormatRef.current)
    )
  }, [trackedOnChange])

  const debouncedOnChange = useDebouncedCallback(saveLiveContent, 2500) // We debounce to avoid adding too many entries to the version history. Since we already flush on blur, it seemed like 2.5 seconds was a good debounce interval.

  useEffect(
    () => () => {
      debouncedOnChange.flush()
    },
    [debouncedOnChange]
  )

  const liveblocks = useLiveblocksExtension({
    field,
    mentions: false,
    initialContent, // Note: There seems to be a bug where the content gets duplicated if a new user joins a room when initialContent and offlineSupport_experimental are both true, so we aren't using offlineSupport_experimental for now.
  })

  const keyDownAtRef = useRef<number | null>(null)
  const updatedAtRef = useRef<number | null>(null)

  /* Inverted-save-gate + in-flight-write reconcile gate. Read here rather than
   * beside the other flag below because `onUpdate` closes over its value at
   * `useEditor` init time; the ref keeps the gate honest if the flag resolves
   * after the editor is built. */
  const saveOnAnyLocalEdit = useFeatureFlag(featureFlags.editorSaveAnyLocalEdit)
  const saveOnAnyLocalEditRef = useRef(saveOnAnyLocalEdit)
  saveOnAnyLocalEditRef.current = saveOnAnyLocalEdit

  const noteDiagnosticsEnabled = useFeatureFlag(
    featureFlags.noteEditorDiagnostics
  )
  /* Diagnostics only. Pointer events are observed on the document rather than
   * the editor: a click on a toolbar button, a mention popup or a dialog moves
   * the caret as legitimately as a click in the note, and none of those reach
   * the editor's own handlers. Capture phase, so a handler that stops
   * propagation cannot hide one. Passive and returning nothing, so no event
   * is handled differently than before. */
  useEffect(() => {
    if (!noteDiagnosticsEnabled) {
      return
    }
    const onPointerDown = (event: PointerEvent): void => {
      pointerDownAtRef.current = Date.now()
      pointerDownInsideEditorRef.current =
        event.target instanceof Node &&
        (liveEditorRef.current?.view.dom.contains(event.target) ?? false)
      diagnosticsCountsRef.current.pointerDowns += 1
    }
    document.addEventListener('pointerdown', onPointerDown, {
      capture: true,
      passive: true,
    })
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      })
    }
  }, [noteDiagnosticsEnabled])
  noteDiagnosticsRef.current.enabled = noteDiagnosticsEnabled
  /* Coverage summary — the denominator for the silence of every reporter above.
   * On the timer as well as at unmount, because a note left open for a working
   * session would otherwise report nothing until the tab closed, and reporting
   * only on an anomaly makes "no events" unreadable: it cannot be told from a
   * flag that never evaluated on, a clinician who never typed, or an observer
   * bug that swallowed everything. */
  useEffect(() => {
    if (!noteDiagnosticsEnabled) {
      return
    }
    const emitCoverage = (reason: 'enabled' | 'interval' | 'unmount') => {
      observeSafely(() => {
        reportDiagnosticsCoverage({
          noteId: field ?? null,
          mountId,
          contentVersionId: noteDiagnosticsRef.current.contentVersionId,
          msSinceMount: Date.now() - mountedAt,
          reason,
          ...diagnosticsCountsRef.current,
        })
      })
    }
    /* A tab closed mid-session runs no unmount, so this is the one emission
     * such a session is guaranteed to leave: proof the flag was on, and the
     * version it loaded. */
    emitCoverage('enabled')
    const timer = setInterval(() => {
      emitCoverage('interval')
    }, DIAGNOSTICS_COVERAGE_INTERVAL_MS)
    return () => {
      clearInterval(timer)
      emitCoverage('unmount')
    }
  }, [noteDiagnosticsEnabled, field, mountId, mountedAt])
  noteDiagnosticsRef.current.saveOnAnyLocalEdit = saveOnAnyLocalEdit
  noteDiagnosticsRef.current.contentVersionId = contentVersionId ?? null

  const handleContentError = useHandleLiveblocksTiptapContentError()

  const forceSaveRef = useRef<() => void>(() => {})
  const onObservationReferencePersisted = useCallback(() => {
    forceSaveRef.current()
  }, [])

  // Node views follow the participant as it changes, which the extension
  // options below, fixed when the editor is built, do not.
  const editorParticipant = useEditorParticipant(participant)
  const { extensions: tiptapExtensions } = useTiptapExtensions({
    editorParticipant,
    placeholder,
    fileHandlerDocumentReferenceContextRelated,
    disableStarterKitHistory: true, // The Liveblocks extension comes with its own history handling
    onObservationReferencePersisted,
    onMergeTemplateMetadata,
    contextParticipant: participant,
    liveblocksProvidesCommentMark: true,
    observationRecordStatus,
  })

  /* Cmd+Z restore for a just-rejected AI proposal. Reject changes no document
   * content (decoration-only), so the Yjs UndoManager can't carry it; this
   * extension intercepts the key and re-pends instead. Its status-flip callbacks
   * read the latest `projectStatuses` through a ref so the editor is built once
   * (recreating it on every persistence change would tear down the room). */
  const undoRejectStatusRef = useRef<{
    onRepend: (id: string) => void
    onReReject: (id: string) => void
  }>({ onRepend: () => {}, onReReject: () => {} })
  const aiProposalUndo = useMemo(
    () =>
      AIProposalUndoExtension.configure({
        onRepend: (id) => undoRejectStatusRef.current.onRepend(id),
        onReReject: (id) => undoRejectStatusRef.current.onReReject(id),
      }),
    []
  )

  /* Arrow-key entry into a suggestion island. Gated on the same
   * ai-note-suggestions flag as every other island interaction, read through a
   * ref: the flag resolves after this editor is built, and rebuilding the
   * extension list to pick it up would tear down the collaboration room. */
  const noteSuggestionsRef = useRef(false)
  const islandTraversal = useMemo(
    () =>
      IslandTraversalExtension.configure({
        enabled: () => noteSuggestionsRef.current,
      }),
    []
  )

  const extensions = useMemo(
    () => [
      liveblocks,
      ...tiptapExtensions,
      aiProposalUndo,
      islandTraversal,
      AISuggestionTrailingParagraphExtension,
      // Caller-supplied extensions stay last so a host can still override
      // anything above them.
      ...(additionalExtensions ?? []),
    ],
    [
      liveblocks,
      tiptapExtensions,
      aiProposalUndo,
      islandTraversal,
      additionalExtensions,
    ]
  )

  /* A late `additionalExtensions` is dropped on the floor (see the prop's
   * comment) — no error, no visual cue, just a feature that never runs. The
   * realistic way to get here is an `enabled` flag that resolves after mount;
   * the portal blocks children on `values.isPending` today, so this should
   * stay quiet. */
  const initialAdditionalExtensions = useRef(additionalExtensions)
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      additionalExtensions !== initialAdditionalExtensions.current
    ) {
      console.warn(
        '[CollaborativeEditor] `additionalExtensions` changed identity after mount. ' +
          'TipTap registers extensions once, at editor construction, so the new ' +
          'ones are ignored and any ProseMirror plugin they add will never run.'
      )
    }
  }, [additionalExtensions])

  const editor = useEditor({
    immediatelyRender: false, // Based on https://github.com/liveblocks/liveblocks/blob/c3fa764ce0d34c207120144b5a9cf2d4068e6102/starter-kits/nextjs-starter-kit/components/TextEditor/TextEditor.tsx#L47
    // Re-render on every transaction so toolbar / selection-driven UI stays in sync with editor state.
    shouldRerenderOnTransaction: true,
    editable: false, // Start read-only, updated after `canWrite` is loaded. From https://github.com/liveblocks/liveblocks/blob/bf62233aba3503d3e82fe3958de32fd2cfbe61b5/starter-kits/nextjs-starter-kit/components/TextEditor/TextEditor.tsx#L48
    extensions,
    parseOptions: {
      // ##PreserveWhitespace Prevents spaces between adjacent inline nodes from collapsing.
      preserveWhitespace: true,
    },
    onFocus: () => {
      // If onFocus happens immediately after onUpdate, it's probably because the user made an update (i.e. changed a section type, entered an @-mention, etc…)
      const isRightAfterUpdate =
        updatedAtRef.current && Date.now() - updatedAtRef.current < 100
      if (isRightAfterUpdate) {
        debouncedOnChange()
      }
    },
    onBlur: () => {
      debouncedOnChange.flush()
    },
    onUpdate: ({ transaction }) => {
      updatedAtRef.current = Date.now()
      const isRightAfterKeyDown =
        keyDownAtRef.current && Date.now() - keyDownAtRef.current < 100
      // See shouldQueueSave. The narrow branch covers an accept's completion
      // patch (the chip's frozen versioned reference), which lands whole FHIR
      // round trips after the click and so never coincides with a keystroke;
      // the inverted branch covers it and every other mouse-driven edit at once.
      if (
        shouldQueueSave(
          transaction,
          Boolean(isRightAfterKeyDown),
          saveOnAnyLocalEditRef.current
        )
      ) {
        debouncedOnChange()
      }
    },
    /* Observation-only caret-jump diagnostics. Wired to `onTransaction` rather
     * than `onUpdate` because a writer passing `emitUpdate: false` skips
     * `onUpdate` entirely, and a silent whole-document write is exactly the
     * thing this is looking for. Emits nothing and allocates nothing when the
     * flag is off. */
    onTransaction: ({
      editor: activeEditor,
      transaction,
      appendedTransactions,
    }) => {
      if (!hasLocalEditRef.current) {
        const batch = [transaction, ...appendedTransactions]
        const source = classifyTransactionSource(batch)
        if (
          batch.some((tr) => tr.docChanged) &&
          (source === 'local' || source === 'undoRedo')
        ) {
          hasLocalEditRef.current = true
          setHasLocalEdit(true)
        }
      }
      const diagnostics = noteDiagnosticsRef.current
      if (!diagnostics.enabled) {
        return
      }
      /* Wraps the WHOLE observation, not just the emission: everything below
       * runs inside a ProseMirror dispatch, where a throw would cost the
       * clinician the note this exists to watch. */
      observeSafely(() => {
        /* The dispatched transaction plus every transaction a plugin appended
         * to it. tiptap reports editor state as it stands after the whole
         * batch, so measuring a position against the root alone compares two
         * different moments and invents or hides drift accordingly. */
        const batch = [transaction, ...appendedTransactions]
        const counts = diagnosticsCountsRef.current
        const now = Date.now()
        const caretBefore = caretBeforeRef.current
        const caretAfter = activeEditor.state.selection.head
        const docChanged = batch.some((tr) => tr.docChanged)
        const selectionSet = batch.some((tr) => tr.selectionSet)
        const drift = measureCaretDrift(batch, caretBefore, caretAfter)
        const step = largestStep(batch)
        const source = classifyTransactionSource(batch)
        const trigger = transactionTrigger({
          caretDrift: drift?.caretDrift ?? null,
          stepCoverage: step.stepCoverage,
        })

        counts.transactions += 1
        if (docChanged) {
          counts.docChangedTransactions += 1
        }
        if (selectionSet) {
          counts.selectionSetTransactions += 1
        }
        if (trigger === 'drift' || trigger === 'both') {
          counts.driftTriggers += 1
        }
        if (trigger === 'replace' || trigger === 'both') {
          counts.replaceTriggers += 1
        }

        if (trigger !== null && emissionBudgetRef.current.tryConsume(now)) {
          const sinceOrNull = (at: number | null): number | null =>
            at === null ? null : now - at
          counts.transactionsEmitted += 1
          reportTransactionObserved({
            noteId: field ?? null,
            mountId,
            contentVersionId: diagnostics.contentVersionId,
            msSinceMount: now - mountedAt,
            trigger,
            source,
            docChanged,
            selectionSet,
            appendedCount: appendedTransactions.length,
            caretBefore,
            caretExpected: drift?.caretExpected ?? null,
            caretDrift: drift?.caretDrift ?? null,
            caretAfter,
            ...batchContentMetrics(batch),
            ...step,
            msSinceKeyDown: sinceOrNull(keyDownAtRef.current),
            lastKeyClass: lastKeyClassRef.current,
            msSincePointerDown: sinceOrNull(pointerDownAtRef.current),
            lastPointerDownInsideEditor: pointerDownInsideEditorRef.current,
            msSinceLastLocalEdit: sinceOrNull(lastLocalEditAtRef.current),
            msSinceLastSave: sinceOrNull(lastSaveAtRef.current),
            msSinceLastReplace: sinceOrNull(lastReplaceAtRef.current),
            editorFocused: activeEditor.isFocused,
            othersCount: diagnostics.othersCount,
            isDocSynchronized: diagnostics.isDocSynchronized,
            saveOnAnyLocalEdit: diagnostics.saveOnAnyLocalEdit,
          })
        }
        counts.transactionsDropped = emissionBudgetRef.current.dropped

        /* Stamped after the report, so the deltas above describe the state
         * this transaction walked into rather than the one it produced. */
        if (trigger === 'replace' || trigger === 'both') {
          lastReplaceAtRef.current = now
        }
        // A doc change this client made and has not necessarily handed to the
        // server yet. `undoRedo` belongs here with `local`: this editor routes
        // Cmd+Z through the Yjs UndoManager, so an undo arrives tagged
        // `isChangeOrigin` like a peer's edit, while `shouldQueueSave`'s
        // keydown branch does persist it. `classifyTransactionSource` already
        // tells this client's undo (y-prosemirror sets `isUndoRedoOperation`
        // from `origin instanceof Y.UndoManager`) from a peer's, which arrives
        // as an ordinary remote change.
        if (docChanged && (source === 'local' || source === 'undoRedo')) {
          lastLocalEditAtRef.current = now
        }
        caretBeforeRef.current = caretAfter
      })
    },
    editorProps: {
      handleDOMEvents: {
        keydown: (view, event) => {
          keyDownAtRef.current = Date.now()
          if (noteDiagnosticsRef.current.enabled) {
            diagnosticsCountsRef.current.keyDowns += 1
            lastKeyClassRef.current = classifyKey(event)
          }
          if (isUndoRedoShortcut(event)) {
            armScrollRestore(view)
          }
        },
      },
      attributes: {
        class: cn(
          'focus-visible:outline-hidden',
          hideRemoteCollaborationCaretLabelOnFocus,
          tiptapVariants()
        ),
      },
    },
    enableContentCheck: true,
    onContentError: handleContentError,
  })

  liveEditorRef.current = editor

  const isReadOnly = useIsReadOnly()
  const canWrite = (useSelf((me) => me.canWrite) || false) && !isReadOnly

  // If canWrite changes, sync to Tiptap, as we're defaulting to false in the config. From https://github.com/liveblocks/liveblocks/blob/bf62233aba3503d3e82fe3958de32fd2cfbe61b5/starter-kits/nextjs-starter-kit/components/TextEditor/TextEditor.tsx#L150C1-L151C1
  if (editor && editor.isEditable !== canWrite) {
    editor.setEditable(canWrite)
  }

  const isEditorReady = useIsEditorReady()
  /* Strictly-stronger companion to `isEditorReady`: true only once the Yjs
   * provider reports "synchronized" (initial content actually arrived), not the
   * "synchronizing" state `isEditorReady` also accepts. Gates the doc-reading
   * reconcile sweep and hydration paint so neither judges a committed accept
   * "orphaned" against a pre-hydration empty doc on a cold reload. */
  const isDocSynchronized = useIsDocSynchronized()
  noteDiagnosticsRef.current.isDocSynchronized = isDocSynchronized

  useEffect(() => {
    forceSaveRef.current = () => {
      // Gate on isEditable so a remote viewer doesn't echo the save back.
      if (!editor || !editor.isEditable) {
        return
      }
      debouncedOnChange.cancel()
      saveLiveContent()
    }
  }, [editor, debouncedOnChange, saveLiveContent])

  useEffect(() => {
    if (!flushPendingSaveRef) {
      return
    }
    flushPendingSaveRef.current = async () => {
      if (!editor || !editor.isEditable) {
        return
      }
      // Always a fresh `onChange` rather than `debouncedOnChange.flush()` —
      // lodash's flush returns the stored result of the last invocation, so
      // awaiting it after a failed autosave would replay the stale rejection.
      debouncedOnChange.cancel()
      await saveLiveContent()
    }
    return () => {
      flushPendingSaveRef.current = null
    }
  }, [editor, debouncedOnChange, saveLiveContent, flushPendingSaveRef])

  useEffect(() => {
    if (!editorRef) {
      return
    }
    editorRef.current = editor
    return () => {
      editorRef.current = null
    }
  }, [editor, editorRef])

  const registryVariableContexts = useRegistryVariableContexts({
    participant,
    encounter,
  })
  useSyncRegistryVariableContexts(editor, registryVariableContexts)
  const conditionalDisplay = useFeatureFlag(featureFlags.structuredCapture)

  const aiRegistry = useOptionalCollaborativeEditorRegistry()

  /* Bumped after each mount-lifecycle whole-doc `setContent` below (the seed
   * and the Medplum reconcile). y-prosemirror reports that reset as ONE
   * whole-document ReplaceStep, so the AIProposals collapse guards drop every
   * painted proposal; the bump re-arms `useAISuggestionHydration`'s one-shot
   * so still-pending persisted suggestions re-resolve against the post-reset
   * doc and re-paint. Without it, hydration (an earlier hook, so an earlier
   * effect) paints first and the reconcile wipe silently erases the batch —
   * the 2026-06-05 reload regression. */
  const [contentResetCount, setContentResetCount] = useState(0)

  /* The ai-note-suggestions gate, resolved once here and threaded to everything
   * it covers. Off: mentionContractFor falls back to insertPolicy 'never' for a
   * condition reference (pre-campaign behavior), so the AI can't stage a NEW
   * condition mention and an accepted side effect never fires for one — see
   * suggestionContracts.ts — and the status bar reports dropped suggestions as
   * a plain count instead of the dialog. Does NOT touch
   * aiResourceRef/aiMetadata/persistence below or StagedSuggestionsBanner,
   * which stay live for the AI chat's unrelated, always-on edit-any-editor
   * capability. */
  const noteSuggestionsEnabled = useFeatureFlag(featureFlags.aiNoteSuggestions)
  noteSuggestionsRef.current = noteSuggestionsEnabled

  /* The ai-citation-mentions gate, resolved here and threaded alongside
   * noteSuggestionsEnabled. Off: mentionContractFor falls back to insertPolicy
   * 'never' for a Composition/QuestionnaireResponse/DocumentReference
   * reference, so the AI can't stage a citation chip. */
  const citationMentionsEnabled = useFeatureFlag(
    featureFlags.aiCitationMentions
  )

  /* Whether a cited choice pick's source is drawn inline under its line (the
   * extension reads the same flag through `useTiptapExtensions`), which is what
   * tells the hover popover not to show it a second time. */
  const inlineChoiceSources = useFeatureFlag(
    featureFlags.aiChatChoiceInlineSource
  )

  /* Persisted AI suggestions (design B2). `resourceRef` is the edited resource's
   * reference (e.g. `Composition/{id}`, `CarePlan/{id}`); surfaces with no
   * resourceRef get undefined and every persistence method no-ops. The registry
   * stays FHIR-free — this hook owns the resource read/patch. */
  const aiResourceRef = aiMetadata?.resourceRef
  const suggestionPersistence = useSuggestionPersistence(aiResourceRef)
  const medplum = useMedplum()

  const isResourceLoaded = suggestionPersistence.isResourceLoaded

  /* Deferred-paint (S5). Per-tab, ephemeral, NOT persisted and NOT shared across
   * tabs. `revealed` = "the suggestion review apparatus is active in THIS tab".
   * The lifecycle (load-edge latch, reveal, hide) lives in
   * `useDeferredSuggestionReveal`. See that hook for the latch semantics.
   *
   * Controlled vs uncontrolled: when a host renders this editor inside a
   * suspense boundary (the visit-note page) it owns the banner — and therefore
   * the reveal state — at the page level so the banner can paint without waiting
   * for the editor to boot. That host threads `deferredReveal` down; we consume
   * it and render NO banner. Every other caller (memos, sticky notes, care-plan
   * blocks) leaves `deferredReveal` undefined, and we own the lifecycle + banner
   * ourselves via the fallback hook below — behavior identical to before. */
  const getEditor = useCallback(() => liveEditorRef.current, [])
  const fallbackReveal = useDeferredSuggestionReveal({
    resourceKey: aiResourceRef,
    isResourceLoaded,
    hasPersistedPending: suggestionPersistence.hasPersistedPending,
    getEditor,
  })
  const revealed = deferredReveal?.revealed ?? fallbackReveal.revealed
  const revealing = deferredReveal?.revealing ?? fallbackReveal.revealing
  const revealGen = deferredReveal?.revealGen ?? fallbackReveal.revealGen
  const handleReveal = fallbackReveal.handleReveal
  const handleRevealSettled =
    deferredReveal?.onRevealSettled ?? fallbackReveal.handleRevealSettled
  const handleHide = deferredReveal?.onHide ?? fallbackReveal.handleHide

  /* Re-arms the hydration one-shot after this editor re-pends records whose
   * decorations it already tore down. Distinct from `revealGen` because that is
   * host-owned state whose bump also flips `revealing` and flashes the banner's
   * loading view; the only consumer today is "Undo rejection of all" (see
   * handleUndoRejectAll). The hook owns the TIMING — see useUndoRejectRepaint. */
  const { repaintGen, requestRepaint } = useUndoRejectRepaint(
    suggestionPersistence.pendingSuggestions.map((s) => s.id)
  )
  const handleLiveStaged =
    deferredReveal?.onLiveStaged ?? fallbackReveal.handleLiveStaged

  /* Register with the AI chat registry, carrying the auto-reveal notification so
   * live staging into a still-deferred note reveals the whole note instead of
   * painting only the new proposals. */
  useEffect(() => {
    if (!editor || !aiRegistry || !aiMetadata) {
      return
    }
    return aiRegistry.register(aiMetadata.id, {
      editor,
      meta: { ...aiMetadata, readOnly: !canWrite },
      onProposalsStaged: handleLiveStaged,
    })
  }, [editor, aiRegistry, aiMetadata, canWrite, handleLiveStaged])

  /* Reveal state for in-editor React surfaces outside the decoration pipeline
   * (the validated-tool node view). Inline proposals gate on `revealed` via the
   * decoration paint; a node view has no decorations, so it reads this directly.
   * Gate on canWrite too: a read-only note must never surface the form's
   * accept/reject overlay (matching the status-bar/hover-popover canWrite gate). */
  const suggestionReveal = useMemo(
    () => ({
      revealed: revealed && canWrite,
      revealing: revealing && canWrite,
    }),
    [revealed, revealing, canWrite]
  )

  /* Serializes the two callbacks below per suggestion. They are both launched
   * from the reconciler's synchronous transaction handler, so a rapid Cmd+Z /
   * Cmd+Shift+Z would otherwise run an unlink and a relink concurrently and let
   * the stale one commit last — accepted content in the note with no
   * Encounter.diagnosis. One queue per editor instance, so it outlives every
   * re-render of the callbacks that feed it. */
  const sideEffectQueue = useMemo(() => createSuggestionSideEffectQueue(), [])

  /* Undo-of-accept compensation (design §O.4 R5). When the reconciler flips an
   * accepted record back to pending on a Cmd+Z, its accept-created FHIR side
   * effects must be voided. Under the no-create model that is unlink-only. The
   * undo removed THIS suggestion's chips, so unlink only the conditions its
   * receipt recorded that NO remaining content node still references — a sibling
   * suggestion tagging the same condition keeps its link (its own receipt owns
   * it). On failure the receipt is left intact so the sign-gate orphan half
   * still blocks and a later compensation can retry. The doc and receipt are
   * read when the queued task RUNS, not when it is enqueued, so a task that
   * waited behind another reconciles against current state. */
  const compensateUndoFlip = useCallback(
    (record: StagedSuggestion) => {
      if (!editor) {
        return
      }
      void sideEffectQueue.run(record.id, () =>
        runUndoFlipCompensation({
          medplum,
          suggestionId: record.id,
          resourceRef: aiResourceRef ?? null,
          doc: editor.state.doc,
          receipt: suggestionPersistence.getReceipt(record.id),
          clearReceiptRefs: suggestionPersistence.clearReceiptRefs,
        })
      )
    },
    [editor, medplum, aiResourceRef, suggestionPersistence, sideEffectQueue]
  )

  /* Redo-of-accept re-apply, the counterpart to compensateUndoFlip: a
   * Cmd+Shift+Z re-inserts the chip, and the reconciler fires this to re-apply
   * the accept's FHIR side effects the intervening undo tore down — relink the
   * encounter and restore the receipt. Side-effects-only: the chip is already
   * back, so this never re-inserts. Idempotent (link-if-absent), so a repeat is
   * a no-op. Queued on the same per-suggestion chain as the compensation above,
   * which is what keeps a completed redo from being followed by the undo's
   * stale unlink. */
  const reapplyRedoFlip = useCallback(
    (record: StagedSuggestion) => {
      if (!editor) {
        return
      }
      void sideEffectQueue.run(record.id, () =>
        reapplyAcceptSideEffects(
          {
            editor,
            persistence: suggestionPersistence,
            medplum,
            resourceRef: aiResourceRef ?? null,
            patientRef: participant?.reference.reference ?? null,
            noteSuggestionsEnabled,
            citationMentionsEnabled,
            onDropped: () => {},
          },
          record.id
        )
      )
    },
    [
      editor,
      medplum,
      aiResourceRef,
      suggestionPersistence,
      noteSuggestionsEnabled,
      citationMentionsEnabled,
      sideEffectQueue,
    ]
  )

  const droppedSuggestions = useAISuggestionHydrationForCollabHost(
    editor,
    isEditorReady,
    aiResourceRef,
    suggestionPersistence,
    noteSuggestionsEnabled,
    citationMentionsEnabled,
    contentResetCount,
    revealed,
    revealGen,
    compensateUndoFlip,
    medplum,
    isDocSynchronized,
    reapplyRedoFlip,
    participant?.reference.reference ?? null,
    repaintGen
  )

  /* Settle the host banner's loading state. Declared immediately AFTER the
   * hydration hook so it runs after that hook's paint effect in the same commit:
   * the conditions below are exactly the paint's preconditions, and the paint is
   * synchronous, so by the time this fires the decorations are in the DOM.
   * The wait it ends is the collaborative doc's connect + initial sync — the
   * paint deliberately blocks on `isDocSynchronized` because resolving against a
   * not-yet-synced (empty) doc would mark every suggestion unplaceable and let
   * the reconcile sweep unlink committed diagnoses. */
  useEffect(() => {
    if (
      !revealing ||
      !editor ||
      !isEditorReady ||
      !isDocSynchronized ||
      !aiResourceRef ||
      !isResourceLoaded
    ) {
      return
    }
    handleRevealSettled()
  }, [
    revealing,
    editor,
    isEditorReady,
    isDocSynchronized,
    aiResourceRef,
    isResourceLoaded,
    handleRevealSettled,
  ])

  /* The full accept pipeline (design §1.3) bound to this editor's persistence.
   * The hover popover and status bar route accepts through it: recheck → insert
   * (stamped) → receipt (R1) → side effects (R2, none registered yet) → status.
   * A recheck failure at accept-click is contained to that one suggestion and
   * surfaced on the same 1.1b dropped list as hydration-time drops. */
  const [acceptDropped, setAcceptDropped] = useState<DroppedSuggestion[]>([])
  const acceptSuggestionPipeline = useCallback(
    (id: string, editedHtml?: string): Promise<void> => {
      if (!editor) {
        return Promise.resolve()
      }
      // Content precedence (design §2.b): an explicit override (Accept-with-edits)
      // wins; otherwise a plain Accept while an island is still open reads that
      // island's LIVE content — the debounced editedContent write is async and
      // can trail, so a plain Accept right after a non-toolbar close (Hide /
      // reveal / supersede) would otherwise fall back to op content. With no open
      // island the pipeline's own `editedContent ?? op` fallback applies.
      const html =
        editedHtml ??
        getIslandRegistry(editor)?.getController(id)?.getCurrentContent()
      return acceptSuggestion(
        {
          editor,
          persistence: suggestionPersistence,
          medplum,
          resourceRef: aiResourceRef ?? null,
          patientRef: participant?.reference.reference ?? null,
          noteSuggestionsEnabled,
          citationMentionsEnabled,
          onDropped: (dropped) => {
            /* The pipeline reports `{ id, label, reason }` only, and its reason
             * is whichever string the failing step produced — a clinician-facing
             * validator refusal, or a model-facing resolve message naming the
             * operation type. Re-describe it from the record so an accept-time
             * drop reads exactly like a reveal-time one and carries the
             * suggestion's own text. */
            const record = suggestionPersistence
              .getAllSuggestions()
              .find((s) => s.id === dropped.id)
            const described: DroppedSuggestion = {
              ...dropped,
              content: droppedSuggestionContent(record),
              reason: clinicianDropReason({
                editor,
                record,
                noteSuggestionsEnabled,
                citationMentionsEnabled,
                reportedReason: dropped.reason,
              }),
            }
            notifyAcceptDrop(described)
            setAcceptDropped((prev) =>
              prev.some((d) => d.id === described.id)
                ? prev
                : [...prev, described]
            )
          },
        },
        id,
        html
      )
    },
    [
      editor,
      suggestionPersistence,
      medplum,
      aiResourceRef,
      noteSuggestionsEnabled,
      citationMentionsEnabled,
    ]
  )
  /* The care plan's questionnaire atoms that cannot be placed. They are listed
   * beside the note's own drops, so one dialog accounts for every suggestion
   * that couldn't be placed and the clinician dismisses them the same way. */
  const publishedQuestionnaireDrops = useDroppedQuestionnaireAtoms()
  const questionnaireDrops = useMemo(
    () =>
      ownQuestionnaireDrops(
        suggestionPersistence.pendingSuggestions,
        publishedQuestionnaireDrops
      ),
    [suggestionPersistence.pendingSuggestions, publishedQuestionnaireDrops]
  )

  /* Merge accept-time drops into the hydration drop list, filtered to records
   * still pending (a later reject/accept removes them) and de-duped against the
   * hydration list. Filtered at read time so no separate cleanup effect is
   * needed. The questionnaire's rows follow; their composite ids cannot match
   * a record id, so they need no de-duping. */
  const allDropped = useMemo(() => {
    const pendingIds = new Set(
      suggestionPersistence.pendingSuggestions.map((s) => s.id)
    )
    const hydrationIds = new Set(droppedSuggestions.map((d) => d.id))
    const accepts = acceptDropped.filter(
      (d) => pendingIds.has(d.id) && !hydrationIds.has(d.id)
    )
    return accepts.length === 0 && questionnaireDrops.length === 0
      ? droppedSuggestions
      : [...droppedSuggestions, ...accepts, ...questionnaireDrops]
  }, [
    droppedSuggestions,
    acceptDropped,
    questionnaireDrops,
    suggestionPersistence.pendingSuggestions,
  ])

  /* Pending validated-tool and questionnaire suggestions never paint as
   * decorations (their own surface reads each record off the note for its
   * banner/prefill), but they ARE part of the staged-suggestions banner count.
   * Add them to the status bar's count so banner and bar agree post-reveal.
   * Less the questionnaire atoms listed as couldn't-be-placed: the bar reports
   * those through the dropped list, as it does an unplaced note suggestion,
   * and counting them here too would report each twice. */
  const pendingToolOpCount = useMemo(
    () =>
      Math.max(
        0,
        pendingUnpaintedCount(suggestionPersistence.pendingSuggestions) -
          questionnaireDrops.length
      ),
    [suggestionPersistence.pendingSuggestions, questionnaireDrops]
  )

  /* The questionnaire slice of that same count, so the bulk controls show for a
   * care plan whose suggestions never paint. Kept separate from the count
   * above, never added to it: the bar already counts these atoms once through
   * `pendingToolOpCount`. The listed atoms are left out here too, so a care
   * plan whose only remaining atoms couldn't be placed offers no sweep that
   * would decide nothing — the same as a note whose only suggestions are
   * unplaced. */
  const pendingQuestionnaireCount = useMemo(
    () =>
      Math.max(
        0,
        pendingQuestionnaireAtomCount(
          suggestionPersistence.pendingSuggestions
        ) - questionnaireDrops.length
      ),
    [suggestionPersistence.pendingSuggestions, questionnaireDrops]
  )

  /* And the validated-tool slice, for the same reason: a visit note whose only
   * AI review is a staged Braden paints nothing either. Kept separate from
   * `pendingToolOpCount` and never added to it. */
  const pendingValidatedToolCount = useMemo(
    () =>
      pendingValidatedToolAtomCount(suggestionPersistence.pendingSuggestions),
    [suggestionPersistence.pendingSuggestions]
  )

  /* Persist EXPLICIT clinician rejects (build-spec §4): unlike accepts (which
   * the content-derived reconciler projects), a reject changes no editor
   * content, so it must ride the click path into the same debounced
   * projection. Without it a rejected suggestion stays 'pending' in the store
   * and resurrects on the next re-paint/reload. */
  const projectStatuses = suggestionPersistence.projectStatuses
  const handleProposalReject = useCallback(
    (ids: string[]) => {
      projectStatuses(ids.map((id) => ({ id, status: 'rejected' as const })))
    },
    [projectStatuses]
  )

  /* Dismiss from the couldn't-be-placed dialog. A note suggestion's row is its
   * whole record, rejected as any other; a questionnaire row is one atom of a
   * record whose other answers may still be live, so it goes back to the
   * questionnaire that listed it and is decided there like a card's Reject. */
  const handleDroppedDismiss = useCallback(
    (ids: string[]) =>
      dismissDroppedRows(
        ids,
        handleProposalReject,
        dismissDroppedQuestionnaireAtom
      ),
    [handleProposalReject]
  )

  /* Reject persistence for node views that decide proposals outside the hover
   * popover (the choice node view's reject-and-apply click). Same projection as
   * the popover's Reject, delivered via context because a node view can't take
   * props from here. */
  const proposalActions = useMemo(
    () => ({ onReject: handleProposalReject }),
    [handleProposalReject]
  )

  /* Island reject (design §2.b): the same effect as the hover popover's Reject —
   * drop the decoration, record it for Cmd+Z re-pend, persist 'rejected' — but
   * invoked from the island toolbar. */
  const handleIslandReject = useCallback(
    (id: string) => {
      if (!editor) {
        return
      }
      const proposal = AI_PROPOSALS_PLUGIN_KEY.getState(
        editor.state
      )?.proposals.get(id)
      editor
        .chain()
        .focus(null, { scrollIntoView: false })
        .rejectAIProposal(id)
        .run()
      if (proposal) {
        editor.commands.recordRejectedAIProposals?.([proposal])
      }
      handleProposalReject([id])
    },
    [editor, handleProposalReject]
  )

  /* Island toolbar actions (design §2.b), delivered via context to the
   * host-owned island (which has no direct access to the editor, accept
   * pipeline, or persistence). Accept-with-edits feeds the island's LIVE content
   * through the pipeline (revalidated); persistEdited/clearEdited drive the
   * editedContent durability writes AND mirror the edit onto the painted
   * proposal, so the card the clinician sees behind the closing island already
   * shows what an accept would insert rather than the AI's superseded original
   * (the persisted write is async, and the next re-paint may be many edits
   * away). */
  const projectEditedContent = suggestionPersistence.projectEditedContent
  const islandActions = useMemo<IslandActions>(() => {
    /* The host's own Cmd+Z, run on behalf of an island whose local history is
     * spent: the suggestion-reject re-pend first, then the document
     * UndoManager, so an island-forwarded press composes with the reject stack
     * and the accept flip/compensation reconciler exactly as a host-focused
     * press does. Nothing here distinguishes the two, which is the point — no
     * second undo path to keep in sync.
     *
     * The two pieces of per-keystroke work the host normally does in its
     * keydown handler are re-supplied because that handler cannot fire for a
     * keystroke made inside a widget: the scroll snapshot the whole-doc rebuild
     * needs, and the timestamp `onUpdate` reads to decide whether a change was
     * user-driven and should queue the debounced save. Without the stamp a
     * forwarded undo would not be saved until the next host keystroke or blur.
     *
     * Focus moves to the host only once something has actually been undone —
     * that is what lets the island hand over rather than sit open beside a
     * second live history, and it leaves a dead press (nothing left on either
     * surface) with the clinician still in their island. */
    const runHostHistory = (direction: 'undo' | 'redo'): boolean => {
      if (!editor?.isEditable) {
        return false
      }
      keyDownAtRef.current = Date.now()
      lastKeyClassRef.current = 'shortcut'
      armScrollRestore(editor.view)
      const ran =
        direction === 'undo'
          ? editor.commands.undoAIProposalReject() ||
            (editor.commands.undo?.() ?? false)
          : editor.commands.redoAIProposalReject() ||
            (editor.commands.redo?.() ?? false)
      if (ran) {
        editor.view.focus()
      }
      return ran
    }
    return {
      acceptWithEdits: (id, html) => acceptSuggestionPipeline(id, html),
      reject: handleIslandReject,
      persistEdited: (id, html) => {
        void projectEditedContent(id, html)
        editor?.commands.setAIProposalEditedContent(id, html)
      },
      clearEdited: (id) => {
        void projectEditedContent(id, undefined)
        editor?.commands.setAIProposalEditedContent(id, undefined)
      },
      hostUndo: () => runHostHistory('undo'),
      hostRedo: () => runHostHistory('redo'),
      exitToHost: (id, side) => {
        if (!editor) {
          return false
        }
        const proposal = AI_PROPOSALS_PLUGIN_KEY.getState(
          editor.state
        )?.proposals.get(id)
        if (!proposal) {
          return false
        }
        const pos = islandExitPos(editor.state.doc, proposal.to, side)
        if (pos === null) {
          return false
        }
        editor.commands.setTextSelection(pos)
        // view.focus() rather than the focus COMMAND: the command defers the
        // real focus into a requestAnimationFrame, which would leave a window
        // where the next keypress lands on a detached island or on body.
        editor.view.focus()
        return true
      },
    }
  }, [
    acceptSuggestionPipeline,
    handleIslandReject,
    projectEditedContent,
    editor,
  ])

  /* Reject All — must cover BOTH placed (decorated) and unplaced (droppedCount)
   * suggestions. The status bar only knows the placed ids it snapshotted from
   * the plugin; we union them with every persisted-pending id so unplaced
   * suggestions are rejected too and their droppedCount returns to zero.
   * Reads pendingSuggestions via a ref so the callback stays stable even as
   * the pending list changes. */
  const bulkPendingRef = useRef(suggestionPersistence.pendingSuggestions)
  bulkPendingRef.current = suggestionPersistence.pendingSuggestions
  /* The questionnaire atoms the last sweep decided, so its undo can return
   * exactly those to undecided and leave alone any atom the clinician had
   * already settled by hand. A ref, not state, and a single slot: the status bar
   * keeps at most one batch armed, and holding it here rather than routing the
   * restore through the per-questionnaire executor registry is what makes the
   * batch whole — an executor answers only while its questionnaire is mounted,
   * whereas these projections write the same staging Basic the editor already
   * holds, so an unmounted questionnaire cannot leave part of a batch stuck
   * rejected. It can afford to: rejecting an answer writes nothing into the
   * QuestionnaireResponse, so no compensating patch (the one thing that does
   * need the mounted hook) is involved. */
  const rejectedAtomsRef = useRef<RejectedQuestionnaireAtom[]>([])
  // The validated-tool subscales the same sweep decided, held beside them so one
  // undo puts back both kinds.
  const rejectedToolFieldsRef = useRef<DecidedValidatedToolField[]>([])
  // Swept questionnaire records, so the undo leaves their status to the re-pend.
  const rejectedQuestionnaireIdsRef = useRef<string[]>([])
  const projectFieldDecisions = suggestionPersistence.projectFieldDecisions
  const rependQuestionnaireAnswers =
    suggestionPersistence.rependQuestionnaireAnswers
  const handleRejectAll = useCallback(
    (placedIds: string[]): string[] => {
      const { ids, atoms, validatedToolFields, questionnaireIds } =
        applyRejectAll(bulkPendingRef.current, placedIds, {
          projectStatuses,
          projectFieldDecisions,
          rependQuestionnaireAnswers,
        })
      rejectedAtomsRef.current = atoms
      rejectedToolFieldsRef.current = validatedToolFields
      rejectedQuestionnaireIdsRef.current = questionnaireIds
      // Return the full set so the status bar can arm rejectedBatch with ALL ids
      // (placed + unplaced) — the undo button must be able to re-pend both.
      return ids
    },
    [projectStatuses, projectFieldDecisions, rependQuestionnaireAnswers]
  )

  /* Undo Reject All — re-projects the batch back to pending, and bumps the
   * repaint generation so the decorations actually come back.
   *
   * Re-pending alone is not enough for PLACED suggestions: Reject All removed
   * their decorations, and the hydration paint is a one-shot latched on
   * `resourceRef#contentResetCount#remoteWipeGen#revealGen#repaintGen` per
   * editor instance. `pendingSuggestions` is not part of that key, so growing
   * it re-renders nothing — which is why, before this bump existed, the undo
   * restored the records in FHIR while the editor kept showing the rejected
   * view until a reload built a fresh editor. Bumping is idempotent-safe:
   * hydration consults `getLatestStatus`, so records decided in the meantime
   * are not resurrected by the extra pass. The re-arm deliberately waits for the
   * re-pended ids to REACH the pending set: projectStatuses is a PATCH plus a
   * refetch, and a pass armed at click time would run while the records still
   * read rejected, paint nothing, and consume the latch again.
   *
   * UNPLACED suggestions need the bump just as much, for a different reason:
   * the effect keyed on `pendingIdSignature` only PRUNES dropped entries that
   * are no longer pending, and nothing re-adds one. Reject All therefore empties
   * the dropped list, and only a fresh hydration pass — which recomputes the
   * whole list from paintPendingProposals — puts those rows back. */
  const handleUndoRejectAll = useCallback(
    (ids: string[]) => {
      const flipped = applyUndoRejectAll(
        ids,
        rejectedAtomsRef.current,
        rejectedQuestionnaireIdsRef.current,
        {
          projectStatuses,
          projectFieldDecisions,
          rependQuestionnaireAnswers,
        },
        rejectedToolFieldsRef.current
      )
      rejectedAtomsRef.current = []
      rejectedToolFieldsRef.current = []
      rejectedQuestionnaireIdsRef.current = []
      /* Wait only on the flips that can REACH the pending set the latch reads.
       * A record a later run shadowed is flipped back to pending, but the newer
       * record holds its slot, so it never appears in `pendingSuggestions` —
       * awaiting it would hang the one-shot and leave every decoration the
       * sweep tore down unpainted until a reload. Same hazard the questionnaire
       * exclusion inside applyUndoRejectAll answers, reached the other way. */
      const awaitable = liveAfterRepend(
        suggestionPersistence.getAllSuggestions(),
        flipped
      )
      if (awaitable.length > 0) {
        requestRepaint(awaitable)
      }
    },
    [
      projectStatuses,
      projectFieldDecisions,
      rependQuestionnaireAnswers,
      requestRepaint,
      suggestionPersistence.getAllSuggestions,
    ]
  )

  /* Accept All, questionnaire half. The inline proposals are accepted by the
   * status bar through the single-suggestion pipeline; every still-undecided
   * questionnaire answer is written here, and the restore this hands back is
   * what the bar arms its one-press undo with. Null when the sweep wrote no
   * answers, so no entry is recorded for a batch that did nothing. */
  const handleAcceptAllQuestionnaires = useCallback(async (): Promise<
    (() => void) | null
  > => {
    const projections = {
      projectStatuses,
      projectFieldDecisions,
      rependQuestionnaireAnswers,
    }
    const accepted = await applyAcceptAllQuestionnaires(
      bulkPendingRef.current,
      projections
    )
    /* Nothing is said here about answers this sweep could not place: they are
     * already listed with the suggestions that couldn't be placed, and stay
     * there until dismissed. The `unplaceableAtomKeys` the executor reports
     * are read only to decide whether the record settles. */
    if (accepted.length === 0) {
      return null
    }
    return () => {
      void applyUndoAcceptAllQuestionnaires(accepted, projections).then(
        (unreversed) => {
          if (unreversed > 0) {
            toast.error(BULK_ACCEPT_NOT_UNDONE)
          }
        }
      )
    }
  }, [projectStatuses, projectFieldDecisions, rependQuestionnaireAnswers])

  /* Accept All, validated-tool half. Staging only — it fills each tool's form
   * by deciding its subscales, and never submits: the QuestionnaireResponse is
   * still the clinician's own press of Submit. Null when the sweep decided
   * nothing, so no undo entry is recorded for a batch that did nothing. */
  const handleAcceptAllValidatedTools = useCallback(
    (): (() => void) | null =>
      applyAcceptAllValidatedTools(bulkPendingRef.current, {
        projectStatuses,
        projectFieldDecisions,
      }),
    [projectStatuses, projectFieldDecisions]
  )

  /* Status-flip side of an interceptor undo/redo (AIProposalUndoExtension):
   * Cmd+Z after a reject re-pends the record, Cmd+Shift+Z re-rejects it. The
   * decoration is repainted by the extension; here we keep the persisted status
   * in step. Kept on a ref so the once-built editor's extension always calls the
   * latest projection. */
  undoRejectStatusRef.current = {
    onRepend: (id) => projectStatuses([{ id, status: 'pending' as const }]),
    onReReject: (id) => projectStatuses([{ id, status: 'rejected' as const }]),
  }

  /* Persist EXPLICIT clinician accepts from the hover popover (build-spec §4a):
   * the content-derived reconciler would project pending→accepted too, but it
   * also runs on FORWARD EDITS after accept and would flip accepted→pending when
   * the content no longer exactly matches. An explicit decidedAt write here
   * gates that flip: the reconciler's accepted→pending branch only fires on undo
   * transactions (where decidedAt has been cleared by the undo). */
  const handleProposalAccept = useCallback(
    (id: string) => {
      projectStatuses([{ id, status: 'accepted' as const }])
    },
    [projectStatuses]
  )

  /* Feed the registry the persisted-pending count so readEditor's
   * hasPendingProposals ORs it in (the agent must see pending suggestions even
   * before they hydrate into live decorations). Stable provider closure reads
   * the latest count through a ref. */
  const persistencePendingRef = useRef(suggestionPersistence.pendingSuggestions)
  persistencePendingRef.current = suggestionPersistence.pendingSuggestions
  useEffect(() => {
    if (!aiRegistry || !aiMetadata || !aiResourceRef) {
      return
    }
    return aiRegistry.setPersistedPendingProvider(
      aiMetadata.id,
      () => persistencePendingRef.current.length
    )
  }, [aiRegistry, aiMetadata, aiResourceRef])

  /* Liveblocks' built-in `initialContent` only seeds when the room's Y.Doc is
   * empty (it sets a `hasContentSet` flag at the doc level, not per-field).
   * For rooms that host multiple editors (participant rooms with memo + visit
   * notes), as soon as one field has content the seed is permanently off for
   * every other field. Opt in to per-field seeding by populating
   * `seededFields` in the room's `initialStorage`; rooms that don't opt in
   * fall through to Liveblocks' default behavior. */
  const seededState = useStorage((root) => {
    if (!field || !root?.seededFields) {
      return 'skip' as const
    }
    return root.seededFields[field]
      ? ('seeded' as const)
      : ('not-seeded' as const)
  })
  const markSeeded = useMutation(
    ({ storage }) => {
      if (!field) {
        return
      }
      storage.get('seededFields')?.set(field, true)
    },
    [field]
  )
  /* `useMutation`'s callback isn't referentially stable across renders (lint
   * flags it in effect dep arrays), so route invocation through a ref to keep
   * the seeding effect's deps clean without disabling exhaustive-deps. */
  const markSeededRef = useRef(markSeeded)
  markSeededRef.current = markSeeded
  useEffect(() => {
    if (!isEditorReady || !editor || !initialContent) {
      return
    }
    if (seededState === null) {
      // Storage still loading
      return
    }
    if (seededState !== 'not-seeded') {
      return
    }
    const currentHtml = getCleanedHtml(editor)
    if (currentHtml !== '' && currentHtml !== '<p></p>') {
      // Field already has content; don't clobber it. Mark seeded so future
      // mounts skip the check.
      markSeededRef.current()
      return
    }
    markSeededRef.current()
    /* preserveWhitespace keeps block-edge whitespace from collapsing so the seed
     * lands at the same canonical fixed point the reconcile compares against
     * (matches the editor-level parseOptions and useSyncEditorContent).
     * addToHistory:false (setContentWithoutHistory) keeps this machine seed off
     * the undo stack — it's never a user edit, so a later Cmd+Z must not pop it. */
    setContentWithoutHistory(editor, initialContent, {
      parseOptions: { preserveWhitespace: true },
    })
    setContentResetCount((c) => c + 1)
  }, [isEditorReady, editor, seededState, initialContent])

  /* Mount-time Medplum -> Liveblocks content reconcile (recovers a room whose
   * synchronized storage genuinely drifted from Medplum, e.g. fallback-editor
   * edits during an outage). Extracted to a hook so its gate + committed-accept
   * guard logic is directly testable — see useMountReconcile. */
  const others = useOthers()
  noteDiagnosticsRef.current.othersCount = others.length
  /* One throttle per editor instance: the reconcile effect re-runs on every
   * autosave, so only a CHANGE of outcome is worth a data point. */
  const reconcileDecisionThrottle = useMemo(
    () => createReconcileDecisionThrottle(),
    []
  )
  const handleReconcileDecision = useCallback(
    (decision: ReconcileDecision) => {
      /* Guarded because the hook calls this SYNCHRONOUSLY on the line before it
       * writes: an unguarded throw out of the document walk below would skip
       * the reconcile itself, turning an observer into an editor bug. */
      observeSafely(() => {
        const counts = diagnosticsCountsRef.current
        counts.reconcileEvaluations += 1
        if (!reconcileDecisionThrottle.shouldEmit(decision.outcome)) {
          return
        }
        counts.reconcileDecisionsEmitted += 1
        // Content sizes are measured here, past the throttle, so the document
        // walk they need never runs on a repeat outcome.
        reportReconcileDecision({
          noteId: field ?? null,
          mountId,
          contentVersionId: noteDiagnosticsRef.current.contentVersionId,
          msSinceMount: Date.now() - mountedAt,
          ...decision,
          ...reconcileContentStats(liveEditorRef.current, initialContent),
        })
      })
    },
    [mountId, mountedAt, field, initialContent, reconcileDecisionThrottle]
  )
  const bumpContentResetCount = useCallback(
    () => setContentResetCount((c) => c + 1),
    []
  )
  /* Outstanding writes of the edited resource made by THIS client. The Mutation
   * outlives the observer that started it in the MutationCache, so the previous
   * mount's unmount-flush PATCH is still counted here after a fast remount —
   * exactly the window in which the query cache still serves the pre-write
   * `initialContent`. */
  const pendingResourceWrites = useIsMutating({
    predicate: (mutation) =>
      aiResourceRef !== undefined &&
      isRecord(mutation.state.variables) &&
      patchMutationTargetRef(mutation.state.variables) === aiResourceRef,
  })
  const reconcileMountOnly = useFeatureFlag(featureFlags.noteReconcileMountOnly)
  useMountReconcile({
    editor,
    isEditorReady,
    isDocSynchronized,
    othersCount: others.length,
    initialContent,
    aiResourceRef,
    isResourceLoaded,
    hasPendingResourceWrite: saveOnAnyLocalEdit && pendingResourceWrites > 0,
    mountOnly: reconcileMountOnly,
    hasLocalEdit,
    suggestionPersistence,
    onReconciled: bumpContentResetCount,
    onDecision: noteDiagnosticsEnabled ? handleReconcileDecision : undefined,
  })

  const shouldShowLoadedToast = useRef(false)
  useEffect(() => {
    if (isEditorReady && shouldShowLoadedToast.current) {
      toast.success('Editor loaded. Click again to edit.')
      shouldShowLoadedToast.current = false
    }
  }, [isEditorReady])

  // Readiness contract surfaced as data attributes for deterministic waits:
  // collab connection + write access, per-field template seed applied, and
  // autosave status. `contents` keeps the placeholder wrapper layout-neutral.
  const collabReady = isEditorReady && canWrite
  const templateSeeded = seededState === 'seeded'

  if (!isEditorReady) {
    return (
      <div
        className="contents"
        data-collab-ready={String(collabReady)}
        data-template-seeded={String(templateSeeded)}
        data-save-state={saveState}
      >
        <PlaceholderEditor
          value={initialContent}
          onClick={() => {
            shouldShowLoadedToast.current = true
          }}
        />
      </div>
    )
  }

  return (
    <EditorResourceProvider value={aiResourceRef ?? null}>
      <SuggestionRevealProvider value={suggestionReveal}>
        <AIProposalActionsProvider value={proposalActions}>
          <IslandActionsProvider value={islandActions}>
            <div
              className="flex flex-col gap-2"
              data-collab-ready={String(collabReady)}
              data-template-seeded={String(templateSeeded)}
              data-save-state={saveState}
            >
              {/* Uncontrolled mode only: a host passing `deferredReveal` owns the banner
               * itself (rendered outside its suspense boundary), so we must not render a
               * second one here. */}
              {!deferredReveal &&
                aiMetadata &&
                isResourceLoaded &&
                suggestionPersistence.hasPersistedPending &&
                (!revealed || revealing) && (
                  <StagedSuggestionsBanner
                    count={pendingDecisionCount(
                      suggestionPersistence.pendingSuggestions
                    )}
                    onReview={handleReveal}
                    revealing={revealing}
                    stickyTopClassName={aiProposalStickyTopClassName}
                  />
                )}
              {/* The status bar + hover popover are the REVEALED review apparatus. Gate
               * both on `revealed` so an unrevealed deferred note shows ONLY the banner
               * (#4/#5/#9 — no double-display) and a stale hover popover can't fire
               * onReject after a Hide. This mirrors the `revealed`-gated hydration
               * effects: decorations only exist while revealed, so the surfaces that act
               * on them only mount while revealed. Live generation is unaffected — a
               * clean note latches `revealed = true` at load. */}
              {aiMetadata && revealed && (
                <AIProposalStatusBar
                  editor={editor}
                  noteSuggestionsEnabled={noteSuggestionsEnabled}
                  dropped={allDropped}
                  pendingToolOpCount={pendingToolOpCount}
                  pendingQuestionnaireCount={pendingQuestionnaireCount}
                  pendingValidatedToolCount={pendingValidatedToolCount}
                  acceptSuggestion={acceptSuggestionPipeline}
                  onAcceptAllQuestionnaires={handleAcceptAllQuestionnaires}
                  onAcceptAllValidatedTools={handleAcceptAllValidatedTools}
                  onReject={handleDroppedDismiss}
                  onRejectAll={handleRejectAll}
                  onUndoRejectAll={handleUndoRejectAll}
                  onHide={handleHide}
                  stickyTopClassName={aiProposalStickyTopClassName}
                />
              )}
              {/* `isolate` gives the editor body its own stacking context, so every
               * z-index inside it (the hover popover, any future overlay) is trapped
               * and can never paint over AIProposalStatusBar / StagedSuggestionsBanner
               * above — siblings outside this boundary — regardless of what z-index
               * value they use. The sticky bar wins by construction, not by numeric
               * agreement with this subtree. */}
              <div className="isolate">
                <EditorParticipantContext.Provider value={editorParticipant}>
                  <EditorContent editor={editor} />
                  {conditionalDisplay && (
                    <>
                      <ConditionValueFeed
                        editor={editor}
                        participant={editorParticipant}
                      />
                      <NodeConfigSheetHost editor={editor} />
                    </>
                  )}
                  <FloatingToolbar editor={editor} />
                </EditorParticipantContext.Provider>
                {aiMetadata && revealed && (
                  <AIProposalHoverPopover
                    editor={editor}
                    acceptSuggestion={acceptSuggestionPipeline}
                    onBeforeAccept={suggestionPersistence.guardAccept}
                    onAccept={handleProposalAccept}
                    onReject={handleProposalReject}
                    inlineChoiceSources={inlineChoiceSources}
                  />
                )}
              </div>
            </div>
          </IslandActionsProvider>
        </AIProposalActionsProvider>
      </SuggestionRevealProvider>
    </EditorResourceProvider>
  )
}

/* Reconciliation and seed-empty checks compare against the HTML
 * `initialContent`, so they always serialize the editor in HTML space —
 * independent of the persistence `outputFormat`. Matches the original
 * pre-Phase-2 helper: sanitize + collapse a single-line <p> wrapper. The
 * stripEmptyEdgeParagraphs step the persistence helper applies is omitted
 * here to keep reconciliation behavior unchanged. */
function getCleanedHtml(editor: Editor): HTML {
  const html = editor.getHTML()
  const sanitizedHtml = DOMPurify.sanitize(html)
  return stripPTagIfOneLine(sanitizedHtml)
}
